/* CEME 3D Design Tool: demo closet configurator (sample catalog, no real checkout). */
(() => {
  if (customElements.get('ceme-design-tool')) return;

  const U = 2540; // internal length unit: 0.01 mm
  const T = 0.75; // panel thickness (in)
  const PL = 3; // top of bottom panel (in)
  const SNAP = 1270; // 0.5 in
  const HISTORY_LIMIT = 50;
  const SCHEMA = 1;

  const inch = (u) => u / U;
  const toU = (v) => Math.round(v * U);
  const n = (v) => +(+v).toFixed(2);
  const clamp = (v, lo, hi) => Math.max(lo, Math.min(hi, v));
  const uid = () => Math.random().toString(36).slice(2, 9);
  const esc = (s) => String(s ?? '').replace(/[&<>"']/g, (c) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[c]));
  const lines = (s) => String(s || '').split(/\r?\n/).map((l) => l.trim()).filter(Boolean);
  const list = (s) => String(s || '').split(',').map((l) => l.trim()).filter(Boolean);

  function shade(hex, amt) {
    let h = String(hex || '#dad8d0').replace('#', '');
    if (h.length === 3) h = h.split('').map((c) => c + c).join('');
    const v = parseInt(h, 16);
    if (Number.isNaN(v)) return hex;
    const t = amt < 0 ? 0 : 255;
    const p = Math.abs(amt);
    const mix = (c) => Math.round((t - c) * p + c);
    const r = mix((v >> 16) & 255);
    const g = mix((v >> 8) & 255);
    const b = mix(v & 255);
    return `#${((1 << 24) | (r << 16) | (g << 8) | b).toString(16).slice(1)}`;
  }

  const ico = (d, vb = '0 0 24 24') => `<svg viewBox="${vb}" aria-hidden="true" focusable="false" fill="none" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round">${d}</svg>`;
  const I = {
    check: ico('<path d="M5 12.5l4.2 4.2L19 7"/>'),
    undo: ico('<path d="M9 14L4 9l5-5"/><path d="M4 9h10a6 6 0 010 12h-3"/>'),
    redo: ico('<path d="M15 14l5-5-5-5"/><path d="M20 9H10a6 6 0 000 12h3"/>'),
    restart: ico('<path d="M3 12a9 9 0 109-9 9.7 9.7 0 00-6.7 2.8L3 8"/><path d="M3 3v5h5"/>'),
    save: ico('<path d="M5 3h11l3 3v15H5z"/><path d="M8 3v5h8V3M8 21v-7h8v7"/>'),
    help: ico('<path d="M4 15v-3a8 8 0 0116 0v3"/><rect x="2.5" y="14" width="4" height="6" rx="1.5"/><rect x="17.5" y="14" width="4" height="6" rx="1.5"/>'),
    plan: ico('<path d="M4 4h16v16H4z"/><path d="M4 13h7v7M14 4v6h6"/>'),
    wall: ico('<rect x="3" y="4" width="18" height="16" rx="1"/><path d="M9 4v16M15 4v16M9 11h6"/>'),
    cube: ico('<path d="M12 2.8l8 4.6v9.2l-8 4.6-8-4.6V7.4z"/><path d="M4 7.4l8 4.6 8-4.6M12 12v9.2"/>'),
    back: ico('<path d="M15 5l-7 7 7 7"/>'),
    collapse: ico('<path d="M12 6l-6 6 6 6M19 6l-6 6 6 6"/>'),
    expand: ico('<path d="M12 6l6 6-6 6M5 6l6 6-6 6"/>'),
    trash: ico('<path d="M4 7h16M9 7V4h6v3M6 7l1 13h10l1-13"/>'),
    copy: ico('<rect x="8" y="8" width="12" height="12" rx="2"/><path d="M16 8V4H4v12h4"/>'),
    left: ico('<path d="M14 6l-6 6 6 6"/>'),
    right: ico('<path d="M10 6l6 6-6 6"/>'),
    close: ico('<path d="M6 6l12 12M18 6L6 18"/>'),
    info: ico('<circle cx="12" cy="12" r="9"/><path d="M12 11v6M12 7.5v.5"/>'),
    warn: ico('<path d="M12 3l10 18H2z"/><path d="M12 10v5M12 18v.5"/>'),
    cart: ico('<path d="M3 4h2l2.4 11h11L21 8H6.2"/><circle cx="9" cy="19.5" r="1.3"/><circle cx="17" cy="19.5" r="1.3"/>'),
    plus: ico('<path d="M12 5v14M5 12h14"/>'),
    minus: ico('<path d="M5 12h14"/>'),
    spark: ico('<path d="M12 3v4M12 17v4M3 12h4M17 12h4M6 6l2.5 2.5M15.5 15.5L18 18M18 6l-2.5 2.5M8.5 15.5L6 18"/>'),
    door: ico('<path d="M6 21V3h12v18M3 21h18"/><path d="M14.5 12v.5"/>'),
    window: ico('<rect x="4" y="3" width="16" height="18" rx="1"/><path d="M12 3v18M4 12h16"/>'),
    outlet: ico('<rect x="6" y="3" width="12" height="18" rx="2"/><path d="M10 8v2M14 8v2M10 15a2 2 0 004 0"/>'),
    switch: ico('<rect x="6" y="3" width="12" height="18" rx="2"/><rect x="10" y="8" width="4" height="8" rx="1"/>'),
    vent: ico('<rect x="3" y="6" width="18" height="12" rx="1"/><path d="M6 10h12M6 14h12"/>'),
    ruler: ico('<path d="M3 17L17 3l4 4L7 21z"/><path d="M7 13l2 2M10 10l2 2M13 7l2 2"/>'),
    layout: ico('<path d="M4 20V4h16"/><path d="M8 20v-8h12"/>'),
    pencil: ico('<path d="M4 20l1-5L16 4l4 4L9 19z"/><path d="M14 6l4 4"/>'),
  };

  const SHAPES = [
    { id: 'reach-in', label: 'Reach In', on: true, d: '<path d="M8 26V9h32v17"/><path d="M8 26h7M33 26h7"/>' },
    { id: 'rectangle', label: 'Rectangle', d: '<rect x="8" y="7" width="32" height="20"/>' },
    { id: 'l-shape', label: 'L-Shape', d: '<path d="M8 27V7h17v9h15v11z"/>' },
    { id: 'angle', label: 'Angle', d: '<path d="M8 27V7h20l12 10v10z"/>' },
    { id: 'custom', label: 'Custom', d: '<rect x="9" y="8" width="30" height="18"/><circle cx="9" cy="8" r="2"/><circle cx="39" cy="8" r="2"/><circle cx="9" cy="26" r="2"/><circle cx="39" cy="26" r="2"/>' },
  ];
  const DOORS = [
    { id: 'single', label: 'Single', d: '<path d="M4 24h10M34 24h10"/><path d="M14 24V6"/><path d="M14 6a18 18 0 0118 18"/>' },
    { id: 'frame', label: 'Frame', d: '<path d="M4 24h10M34 24h10"/><path d="M14 20v8M34 20v8"/>' },
    { id: 'double', label: 'Double', d: '<path d="M4 24h10M34 24h10"/><path d="M14 24v-10M34 24v-10"/><path d="M14 14a10 10 0 0110 10M34 14a10 10 0 00-10 10"/>' },
    { id: 'sliding', label: 'Sliding Door', d: '<path d="M4 24h10M34 24h10"/><rect x="14" y="20" width="12" height="3"/><rect x="22" y="25" width="12" height="3"/>' },
    { id: 'pocket', label: 'Pocket', d: '<path d="M4 24h10M34 24h10"/><rect x="34" y="22.5" width="12" height="3" stroke-dasharray="2 2"/><path d="M14 24h4"/>' },
    { id: 'bifold', label: 'Bifold', d: '<path d="M4 24h10M34 24h10"/><path d="M14 24l5-9 5 9 5-9 5 9"/>' },
  ];
  const FEATURES = {
    window: { label: 'Window', w: 30, h: 36, y: 40 },
    switch: { label: 'Switch', w: 2.75, h: 4.5, y: 48 },
    outlet: { label: 'Outlet', w: 2.75, h: 4.5, y: 16 },
    vent: { label: 'Vent', w: 10, h: 6, y: 82 },
  };
  const CAMS = { front: [0, 0], left: [28, -2], right: [-28, -2] };
  const AUTO_SEQ = ['double_hang', 'long_hang', 'tower_shelves', 'hang_shelves', 'shoe'];

  function evenly(from, to, step) {
    const span = to - from;
    const k = Math.max(1, Math.round(span / step));
    const out = [];
    for (let i = 1; i < k; i += 1) out.push(from + (span * i) / k - T / 2);
    return out;
  }

  function parts(pattern, h) {
    const top = h - T;
    const p = { shelves: [], drawers: [], doors: [], rods: [], hooks: [], fences: [], bench: null };
    const stack = (count, y0, dh) => {
      let y = y0;
      for (let i = 0; i < count; i += 1) { p.drawers.push({ y, h: dh }); y += dh; }
      return y;
    };
    switch (pattern) {
      case 'tower_drawers': { const y = stack(3, PL, 10); p.shelves = [y, ...evenly(y + T, top, 13)]; break; }
      case 'double_hang': { const mid = Math.round(h / 2); p.shelves = [mid]; p.rods = [top - 3, mid - 3]; break; }
      case 'long_hang': p.shelves = [top - 13]; p.rods = [top - 16]; break;
      case 'hang_shelves': { const mid = Math.round(h * 0.45); p.shelves = [...evenly(PL, mid, 12), mid]; p.rods = [top - 3]; break; }
      case 'shoe': { const mid = Math.round(h * 0.55); const s = evenly(PL, mid, 8); p.shelves = [...s, mid, ...evenly(mid + T, top, 13)]; p.fences = s; break; }
      case 'hutch': {
        const y = stack(3, PL, 10);
        const dy = Math.max(y + 12, top - 30);
        p.shelves = [y, ...evenly(y + T, dy, 12), dy];
        p.doors = [{ y: dy + T, h: top - dy - T }];
        break;
      }
      case 'mudroom': p.shelves = [10.5, top - 13]; p.bench = 18; p.hooks = [Math.min(58, top - 18)]; break;
      default: p.shelves = evenly(PL, top, 13);
    }
    return p;
  }
  const leaves = (w) => (w >= 30 ? 2 : 1);
  const handleCount = (pattern, w, h) => { const p = parts(pattern, h); return p.drawers.length + (p.doors.length ? leaves(w) : 0); };

  function moduleSVG(pattern, w, h, x0, sy, c) {
    const p = parts(pattern, h);
    const st = `stroke="${c.edge}" stroke-width="1" vector-effect="non-scaling-stroke"`;
    const R = (x, y, ww, hh, fill, extra = st) => `<rect x="${n(x)}" y="${n(sy(y + hh))}" width="${n(Math.max(ww, 0))}" height="${n(Math.max(hh, 0))}" fill="${fill}" ${extra}/>`;
    const front = (x, y, ww, hh, shaker) => R(x, y, ww, hh, c.base) + (shaker && ww > 5 && hh > 5 ? R(x + 1.6, y + 1.6, ww - 3.2, hh - 3.2, 'none') : '');
    const iw = w - 2 * T;
    const ix = x0 + T;
    let o = R(ix, PL, iw, h - PL - T, c.inner, '');
    o += R(ix + 0.4, 0, iw - 0.8, PL - T, c.dark);
    o += R(x0, 0, T, h, c.base) + R(x0 + w - T, 0, T, h, c.base) + R(ix, h - T, iw, T, c.base) + R(ix, PL - T, iw, T, c.base);
    p.shelves.forEach((y) => { o += R(ix, y, iw, T, c.base); });
    p.fences.forEach((y) => { o += R(ix, y + T, iw, 1.2, 'none'); });
    if (p.bench != null) o += R(ix, p.bench - 1.5, iw, 1.5, c.base);
    p.rods.forEach((y) => { o += R(ix, y, iw, 0.9, '#a9a9a6', 'stroke="#7d7d7b" stroke-width="1" vector-effect="non-scaling-stroke"'); });
    p.hooks.forEach((y) => { [0.25, 0.5, 0.75].forEach((t) => { o += `<circle cx="${n(ix + iw * t)}" cy="${n(sy(y))}" r="0.9" fill="${c.handle}"/>`; }); });
    p.drawers.forEach((d) => {
      o += front(ix + 0.15, d.y + 0.15, iw - 0.3, d.h - 0.3, c.drawer === 'Shaker');
      const hl = Math.min(6, iw * 0.35);
      o += R(ix + iw / 2 - hl / 2, d.y + d.h - 2.6, hl, 0.7, c.handle, '');
    });
    p.doors.forEach((d) => {
      const k = leaves(w);
      const dw = iw / k;
      for (let i = 0; i < k; i += 1) {
        const dx = ix + i * dw;
        o += front(dx + 0.15, d.y, dw - 0.3, d.h - 0.15, c.door === 'Shaker');
        const hx = k === 2 && i === 1 ? dx + 1.4 : dx + dw - 2.1;
        o += R(hx, d.y + 2, 0.7, 5, c.handle, '');
      }
    });
    return o;
  }

  function featureSVG(type, x, y, w, h, sy) {
    const r = (xx, yy, ww, hh, attrs) => `<rect x="${n(xx)}" y="${n(sy(yy + hh))}" width="${n(ww)}" height="${n(hh)}" ${attrs}/>`;
    const st = 'vector-effect="non-scaling-stroke"';
    if (type === 'window') return r(x, y, w, h, `fill="#f4f3ee" stroke="#ffffff" stroke-width="4" ${st}`) + `<path d="M${n(x + w / 2)} ${n(sy(y))}V${n(sy(y + h))}M${n(x)} ${n(sy(y + h / 2))}H${n(x + w)}" stroke="#ffffff" stroke-width="3" ${st}/>`;
    if (type === 'vent') return r(x, y, w, h, `fill="#e7e6e2" stroke="#55605a" stroke-width="1" ${st}`) + [0.3, 0.5, 0.7].map((t) => `<path d="M${n(x + 1)} ${n(sy(y + h * t))}H${n(x + w - 1)}" stroke="#55605a" stroke-width="1" ${st}/>`).join('');
    const inner = type === 'switch' ? r(x + w * 0.32, y + h * 0.3, w * 0.36, h * 0.4, `fill="#e3e2de" stroke="#55605a" stroke-width="1" ${st}`) : r(x + w * 0.3, y + h * 0.55, w * 0.12, h * 0.16, 'fill="#55605a"') + r(x + w * 0.58, y + h * 0.55, w * 0.12, h * 0.16, 'fill="#55605a"');
    return r(x, y, w, h, `fill="#f4f3ee" stroke="#55605a" stroke-width="1" ${st}`) + inner;
  }

  const face = (cls, w, h, X, Y, Z, rot, bg, attrs = '') => `<i class="cdt3-f ${cls}" ${attrs} style="width:${n(w)}px;height:${n(h)}px;margin:${n(-h / 2)}px 0 0 ${n(-w / 2)}px;transform:translate3d(${n(X)}px,${n(Y)}px,${n(Z)}px)${rot ? ` ${rot}` : ''};${bg ? `background-color:${bg};` : ''}"></i>`;

  function box(ctx, x0, y0, z0, w, h, d, col, faces, cls = '', attrs = '') {
    const { s } = ctx;
    const X = (x0 + w / 2 - ctx.W / 2) * s;
    const Y = -(y0 + h / 2 - ctx.H / 2) * s;
    const Z = (z0 + d / 2 - ctx.D / 2) * s;
    let o = '';
    if (faces.includes('f')) o += face(`${cls} is-front`, w * s, h * s, X, Y, Z + (d * s) / 2, '', col, attrs);
    if (faces.includes('t')) o += face(cls, w * s, d * s, X, Y - (h * s) / 2, Z, 'rotateX(90deg)', shade(col, 0.08), attrs);
    if (faces.includes('b')) o += face(cls, w * s, d * s, X, Y + (h * s) / 2, Z, 'rotateX(-90deg)', shade(col, -0.16), attrs);
    if (faces.includes('l')) o += face(cls, d * s, h * s, X - (w * s) / 2, Y, Z, 'rotateY(-90deg)', shade(col, -0.1), attrs);
    if (faces.includes('r')) o += face(cls, d * s, h * s, X + (w * s) / 2, Y, Z, 'rotateY(90deg)', shade(col, -0.1), attrs);
    return o;
  }

  function parseLength(raw, unit) {
    const s = String(raw ?? '').trim().replace(/(in(ches)?|mm|["”])$/i, '').trim();
    if (!s) return { error: 'Enter a value.' };
    let v;
    if (unit === 'mm') {
      if (!/^\d+(\.\d+)?$/.test(s)) return { error: 'Enter a number in millimeters.' };
      v = parseFloat(s) / 25.4;
    } else {
      const m = s.match(/^(\d+(?:\.\d+)?)?(?:[\s-]+)?(?:(\d+)\/(\d+))?$/);
      if (!m || (!m[1] && !m[2])) return { error: 'Enter inches, e.g. 29.5 or 29 1/2.' };
      if (m[3] === '0') return { error: 'Fractions cannot divide by zero.' };
      v = (m[1] ? parseFloat(m[1]) : 0) + (m[2] ? parseInt(m[2], 10) / parseInt(m[3], 10) : 0);
    }
    return { value: toU(v) };
  }

  function normalize(raw) {
    const pairs = (s) => lines(s).map((l) => { const [name, hex] = l.split(':').map((x) => x.trim()); return { name, hex: hex || '#dad8d0' }; }).filter((x) => x.name);
    const num = (v, d) => (Number.isFinite(parseFloat(v)) ? parseFloat(v) : d);
    const cfg = { ...raw };
    cfg.colors = pairs(raw.colors);
    if (!cfg.colors.length) cfg.colors = [{ name: 'White', hex: '#f4f3ee' }];
    cfg.handles = pairs(raw.handles);
    if (!cfg.handles.length) cfg.handles = [{ name: 'Matte Black', hex: '#234236' }];
    cfg.depths = lines(raw.depths).map((l) => {
      const [v, label, desc, tag] = l.split('|').map((x) => x.trim());
      return { value: toU(num(v, 16)), label: label || '', desc: desc || '', tag: tag || '' };
    });
    if (!cfg.depths.length) cfg.depths = [{ value: toU(16), label: 'Standard depth', desc: '', tag: '' }];
    cfg.heights = list(raw.heights).map((v) => toU(num(v, 84))).filter((v) => v > 0);
    if (!cfg.heights.length) cfg.heights = [toU(84)];
    cfg.drawerStyles = list(raw.drawerStyles);
    if (!cfg.drawerStyles.length) cfg.drawerStyles = ['Slab'];
    cfg.doorStyles = list(raw.doorStyles);
    if (!cfg.doorStyles.length) cfg.doorStyles = ['Slab'];
    cfg.handlePrice = Math.round(num(raw.handlePrice, 0) * 100);
    cfg.room = {
      width: num(raw.room?.width, 100), depth: num(raw.room?.depth, 29.5), height: num(raw.room?.height, 96),
      door: num(raw.room?.door, 33), color: raw.room?.color || '#ADA891',
    };
    cfg.lines = (raw.lines || []).filter((l) => l.name).map((l) => ({ ...l, features: lines(l.features) }));
    if (!cfg.lines.length) cfg.lines = [{ id: 'default', name: 'Closet System', subtitle: '', description: '', features: [], style: 'light' }];
    cfg.modules = (raw.modules || []).filter((m) => m.title).map((m) => {
      const widths = list(m.widths).map((v) => num(v, 0)).filter((v) => v >= 6);
      const prices = list(m.prices).map((v) => Math.round(num(v, 0) * 100));
      return { ...m, widths: widths.length ? widths : [24], prices, fixedHeight: num(m.fixedHeight, 0), lines: list(m.lines).map((x) => x.toLowerCase()) };
    });
    cfg.accessories = (raw.accessories || []).filter((a) => a.title).map((a) => ({ ...a, price: Math.round(num(a.price, 0) * 100), max: Math.max(1, Math.round(num(a.max, 10))) }));
    return cfg;
  }

  class CemeDesignTool extends HTMLElement {
    connectedCallback() {
      if (this.ready) return;
      this.ready = true;
      this.app = this.querySelector('[data-cdt-app]');
      try {
        this.cfg = normalize(JSON.parse(this.querySelector('[data-cdt-config]').textContent));
      } catch (e) {
        this.app.innerHTML = '<p class="cdt-fail">The design tool could not be loaded. Please refresh the page.</p>';
        return;
      }
      this.money = new Intl.NumberFormat('en-US', { style: 'currency', currency: this.cfg.currency || 'USD' });
      this.design = this.blank();
      this.ui = { step: 1, maxStep: 1, unit: 'in', view: { 4: 'plan', 5: '3d' }, tab: { 4: 'measure', 5: 'prefs' }, sel: null, selFeat: null, collapsed: false, cam: 'front', yaw: 0, pitch: 0 };
      this.past = [];
      this.future = [];
      this.key = `ceme-design-tool:v${SCHEMA}:${this.cfg.storageId || location.pathname}`;
      this.editor = !!(window.Shopify && window.Shopify.designMode);
      const restored = this.editor ? null : this.restore();
      this.app.innerHTML = '<div class="cdt-root" data-root></div><div class="cdt-toast" role="status" aria-live="polite" data-toast></div>';
      this.root = this.app.querySelector('[data-root]');
      this.toastEl = this.app.querySelector('[data-toast]');
      this.bind();
      this.render();
      if (restored) this.toast(restored);
      if ('ResizeObserver' in window) {
        this.ro = new ResizeObserver(() => { if (this.view() === '3d') this.draw3D(); });
        this.ro.observe(this.root);
      }
      this.setupMobileNote();
      this.setupEditor();
      this.measure();
      this.onResize = () => this.measure();
      window.addEventListener('resize', this.onResize);
      window.addEventListener('load', this.onResize, { once: true });
    }

    disconnectedCallback() {
      this.ro?.disconnect();
      window.removeEventListener('resize', this.onResize);
      document.removeEventListener('pointerdown', this.onDocDown, true);
      document.removeEventListener('pointerup', this.onDocUp, true);
      document.removeEventListener('pointercancel', this.onDocUp, true);
      this.closeDialog(false);
      if (this.onEditor) {
        document.removeEventListener('shopify:block:select', this.onEditor);
      }
    }

    blank() {
      const c = this.cfg;
      return {
        line: c.lines[0].id,
        shape: 'reach-in',
        door: { type: 'single', width: toU(c.room.door), offset: null },
        room: { width: toU(c.room.width), depth: toU(c.room.depth), height: toU(c.room.height), color: c.room.color },
        features: [],
        prefs: { color: c.colors[0].name, depth: c.depths[0].value, height: c.heights[0], drawer: c.drawerStyles[0], door: c.doorStyles[0], handle: c.handles[0].name },
        modules: [],
        acc: {},
      };
    }

    /* ---------- persistence ---------- */
    restore() {
      let data;
      try { data = JSON.parse(localStorage.getItem(this.key) || 'null'); } catch (e) { return null; }
      if (!data || !data.design) return null;
      if (data.schemaVersion !== SCHEMA) return 'A saved design from an older version could not be restored.';
      const d = { ...this.blank(), ...data.design };
      const before = d.modules.length;
      d.modules = (d.modules || []).filter((m) => this.def(m.type));
      if (!this.cfg.lines.some((l) => l.id === d.line)) d.line = this.cfg.lines[0].id;
      this.design = d;
      this.ui.step = clamp(data.step || 1, 1, 5);
      this.ui.maxStep = clamp(data.maxStep || this.ui.step, this.ui.step, 5);
      this.ui.unit = data.unit === 'mm' ? 'mm' : 'in';
      const dropped = before - d.modules.length;
      return dropped > 0 ? `Restored your saved design. ${dropped} module(s) are no longer available and were not restored.` : 'Restored your saved design from this device.';
    }

    persist() {
      if (this.editor) return false;
      try {
        localStorage.setItem(this.key, JSON.stringify({ schemaVersion: SCHEMA, savedAt: new Date().toISOString(), step: this.ui.step, maxStep: this.ui.maxStep, unit: this.ui.unit, design: this.design }));
        return true;
      } catch (e) { return false; }
    }

    scheduleSave() {
      clearTimeout(this.saveTimer);
      this.saveTimer = setTimeout(() => this.persist(), 500);
    }

    /* ---------- model helpers ---------- */
    def(type) { return this.cfg.modules.find((m) => m.id === type); }
    line() { return this.cfg.lines.find((l) => l.id === this.design.line) || this.cfg.lines[0]; }
    available() {
      const name = String(this.line().name || '').toLowerCase();
      return this.cfg.modules.filter((m) => !m.lines.length || m.lines.includes(name));
    }
    mH(m) { const d = this.def(m.type); return d && d.fixedHeight > 0 ? toU(d.fixedHeight) : this.design.prefs.height; }
    mD() { return this.design.prefs.depth; }
    price(m) {
      const d = this.def(m.type);
      if (!d) return 0;
      const i = d.widths.findIndex((w) => toU(w) === m.width);
      const list2 = d.prices.length ? d.prices : [0];
      return list2[clamp(i < 0 ? 0 : i, 0, list2.length - 1)] || 0;
    }
    hex(name, arr) { return (arr.find((c) => c.name === name) || arr[0]).hex; }
    colors() {
      const base = this.hex(this.design.prefs.color, this.cfg.colors);
      return { base, edge: shade(base, -0.32), inner: shade(base, -0.07), dark: shade(base, -0.2), handle: this.hex(this.design.prefs.handle, this.cfg.handles), drawer: this.design.prefs.drawer, door: this.design.prefs.door };
    }
    span() {
      const { door, room } = this.design;
      const w = Math.min(door.width, room.width);
      const a = door.offset == null ? Math.round((room.width - w) / 2) : clamp(door.offset, 0, room.width - w);
      return { a, b: a + w };
    }
    sorted() { return [...this.design.modules].sort((p, q) => p.x - q.x); }
    view() { return this.ui.view[this.ui.step]; }
    len(u, withUnit = true) {
      if (this.ui.unit === 'mm') return `${Math.round(u / 100)}${withUnit ? ' mm' : ''}`;
      return `${n(inch(u))}${withUnit ? '"' : ''}`;
    }
    val(u) { return this.ui.unit === 'mm' ? String(Math.round(u / 100)) : String(n(inch(u))); }
    label(m) {
      const i = this.sorted().findIndex((x) => x.id === m.id);
      return `Module ${i + 1} (${this.def(m.type)?.title || 'Module'})`;
    }

    findGap(w, ignoreId = null) {
      const W = this.design.room.width;
      let x = 0;
      for (const m of this.sorted().filter((q) => q.id !== ignoreId)) {
        if (m.x - x >= w) return x;
        x = Math.max(x, m.x + m.width);
      }
      return W - x >= w ? x : null;
    }

    /* ---------- validation & BOM ---------- */
    validate() {
      const { room, modules, features } = this.design;
      const errors = [];
      const warnings = [];
      const by = {};
      const add = (bucket, m, msg) => { bucket.push(m ? `${this.label(m)}: ${msg}` : msg); if (m) (by[m.id] = by[m.id] || []).push({ level: bucket === errors ? 'error' : 'warn', msg }); };
      const { a, b } = this.span();
      const s = this.sorted();
      s.forEach((m, i) => {
        const h = this.mH(m);
        if (m.x < 0 || m.x + m.width > room.width) add(errors, m, `extends past Wall B by ${this.len(Math.max(m.x + m.width - room.width, -m.x))}.`);
        if (h > room.height) add(errors, m, `is taller than the wall height (${this.len(room.height)}).`);
        if (this.mD() > room.depth) add(errors, m, `is deeper than the closet (${this.len(room.depth)}).`);
        const next = s[i + 1];
        if (next && next.x < m.x + m.width) add(errors, m, 'overlaps the next module.');
        if (this.design.shape === 'reach-in' && (m.x < a || m.x + m.width > b)) add(warnings, m, 'is partly behind the wall beside the door. Check that you can reach it.');
        features.forEach((f) => {
          const F = FEATURES[f.type];
          if (!F) return;
          if (f.x < m.x + m.width && f.x + toU(F.w) > m.x && f.y < h) add(warnings, m, f.type === 'window' ? 'covers part of the window.' : `covers the ${F.label.toLowerCase()}.`);
        });
      });
      return { errors, warnings, by };
    }

    bom() {
      const rows = new Map();
      const c = this.cfg;
      const p = this.design.prefs;
      let handles = 0;
      this.design.modules.forEach((m) => {
        const d = this.def(m.type);
        if (!d) return;
        const k = `${m.type}|${m.width}`;
        const row = rows.get(k) || { name: d.title, detail: `${this.len(m.width)} W × ${this.len(this.mH(m))} H × ${this.len(this.mD())} D · ${p.color}`, qty: 0, unit: this.price(m) };
        row.qty += 1;
        rows.set(k, row);
        handles += handleCount(d.pattern, inch(m.width), inch(this.mH(m)));
      });
      const out = [...rows.values()];
      if (handles) out.push({ name: `Handle · ${p.handle}`, detail: 'Required: 1 per drawer and door front', qty: handles, unit: c.handlePrice, required: true });
      c.accessories.forEach((a) => { const q = this.design.acc[a.id] || 0; if (q > 0) out.push({ name: a.title, detail: a.description || 'Optional accessory', qty: q, unit: a.price }); });
      const total = out.reduce((sum, r) => sum + r.qty * r.unit, 0);
      return { rows: out, total, handles };
    }

    /* ---------- state changes ---------- */
    snap() { return JSON.stringify(this.design); }
    push(s = this.snap()) {
      this.past.push(s);
      if (this.past.length > HISTORY_LIMIT) this.past.shift();
      this.future = [];
    }
    change(fn, msg, defer = false) {
      this.push();
      fn(this.design);
      this.commit(msg, defer);
    }
    commit(msg, defer = false) {
      this.scheduleSave();
      // A field "change" fires on blur at pointerdown; rendering then would swallow the click that caused the blur.
      if (defer && this.ptrDown) this.pending = true;
      else this.render();
      if (msg) this.toast(msg);
    }
    undo() {
      if (!this.past.length) return;
      this.future.push(this.snap());
      this.design = JSON.parse(this.past.pop());
      this.fixSel();
      this.commit('Undone.');
    }
    redo() {
      if (!this.future.length) return;
      this.past.push(this.snap());
      this.design = JSON.parse(this.future.pop());
      this.fixSel();
      this.commit('Redone.');
    }
    fixSel() {
      if (this.ui.sel && !this.design.modules.some((m) => m.id === this.ui.sel)) this.ui.sel = null;
      if (this.ui.selFeat && !this.design.features.some((f) => f.id === this.ui.selFeat)) this.ui.selFeat = null;
    }

    goStep(step) {
      this.ui.step = clamp(step, 1, 5);
      this.ui.maxStep = Math.max(this.ui.maxStep, this.ui.step);
      this.ui.sel = null;
      this.ui.selFeat = null;
      this.persist();
      this.render();
      this.root.querySelector('.cdt-stage')?.focus({ preventScroll: true });
      const top = this.app.getBoundingClientRect().top;
      const bar = parseFloat(getComputedStyle(document.body).getPropertyValue('--header-height')) || 0;
      if (top < bar - 4 || top > window.innerHeight * 0.5) window.scrollTo({ top: window.scrollY + top - bar, behavior: 'smooth' });
    }

    measure() {
      const top = this.app.getBoundingClientRect().top + window.scrollY;
      this.style.setProperty('--cdt-offset', `${Math.round(top)}px`);
    }

    next() {
      const step = this.ui.step;
      if (step === 2 && this.cfg.depths.length > 1) return this.depthDialog();
      if (step === 4 && !this.design.modules.length) return this.startDialog();
      return this.goStep(step + 1);
    }

    prev() {
      const map = { 2: 1, 3: 2, 4: 2, 5: 4 };
      this.goStep(map[this.ui.step] || 1);
    }

    addModule(type) {
      const d = this.def(type);
      if (!d) return;
      const w = toU(d.widths[0]);
      const x = this.findGap(w);
      if (x == null) { this.toast(`Not enough free wall space for ${d.title} (needs ${this.len(w)}).`, 'error'); return; }
      const id = uid();
      this.change((s) => { s.modules.push({ id, type, width: w, x }); }, `${d.title} added.`);
      this.ui.sel = id;
      this.render();
    }

    setWidth(m, w) {
      const others = this.sorted().filter((q) => q.id !== m.id);
      const fits = (x) => x >= 0 && x + w <= this.design.room.width && others.every((q) => x + w <= q.x || x >= q.x + q.width);
      const nextM = others.find((q) => q.x >= m.x);
      const tries = [m.x, nextM ? nextM.x - w : this.design.room.width - w, m.x + m.width - w];
      const x = tries.find((t) => fits(t));
      if (x == null) { this.toast(`There is no room for a ${this.len(w)} wide module here. Move or remove a neighbor first.`, 'error'); this.render(); return; }
      this.change((s) => { const t = s.modules.find((q) => q.id === m.id); t.width = w; t.x = x; }, `Width changed to ${this.len(w)}.`);
    }

    moveBy(id, du) {
      const m = this.design.modules.find((q) => q.id === id);
      if (!m) return;
      const [lo, hi] = this.bounds(m);
      const x = clamp(m.x + du, lo, Math.max(lo, hi));
      if (x === m.x) { this.toast('The module cannot move further in that direction.'); return; }
      this.change((s) => { s.modules.find((q) => q.id === id).x = x; });
    }

    bounds(m) {
      const others = this.design.modules.filter((q) => q.id !== m.id);
      const c = m.x + m.width / 2;
      const lo = Math.max(0, ...others.filter((q) => q.x + q.width / 2 <= c).map((q) => q.x + q.width));
      const hi = Math.min(this.design.room.width, ...others.filter((q) => q.x + q.width / 2 > c).map((q) => q.x)) - m.width;
      return [lo, hi];
    }

    autoPopulate() {
      const W = this.design.room.width;
      const avail = this.available().filter((d) => !(d.fixedHeight > 0));
      const by = (p) => avail.find((d) => d.pattern === p);
      const pick = (d, rem) => {
        if (!d) return null;
        const w = [...d.widths].map(toU).sort((p, q) => q - p).find((x) => x <= rem);
        return w ? { type: d.id, width: w } : null;
      };
      let rem = W;
      const center = pick(by('tower_drawers') || by('hutch'), rem);
      const left = [];
      const right = [];
      if (center) rem -= center.width;
      let i = 0;
      let guard = 0;
      while (guard < 24 && left.length + right.length < 12) {
        guard += 1;
        let placed = null;
        for (let k = 0; k < AUTO_SEQ.length && !placed; k += 1) placed = pick(by(AUTO_SEQ[(i + k) % AUTO_SEQ.length]), rem);
        if (!placed) break;
        (left.length <= right.length ? left : right).push(placed);
        rem -= placed.width;
        i += 1;
      }
      const row = [...left.reverse(), ...(center ? [center] : []), ...right];
      if (!row.length) { this.toast('No sample modules fit this wall. Check the wall width.', 'error'); return false; }
      const total = row.reduce((s, m) => s + m.width, 0);
      let x = Math.floor((W - total) / 2 / SNAP) * SNAP;
      const mods = row.map((m) => { const o = { id: uid(), type: m.type, width: m.width, x }; x += m.width; return o; });
      let heightNote = '';
      const fitH = [...this.cfg.heights].sort((p, q) => q - p).find((h) => h <= this.design.room.height);
      this.change((s) => {
        s.modules = mods;
        if (s.prefs.height > s.room.height && fitH) { s.prefs.height = fitH; heightNote = ` Module height set to ${this.len(fitH)} to fit the wall.`; }
      });
      this.toast(`Suggested layout added: ${mods.length} modules. You can edit or undo it.${heightNote}`);
      return true;
    }

    /* ---------- rendering ---------- */
    render() {
      if (this.rendering) { this.pending = true; return; }
      this.rendering = true;
      this.pending = false;
      const active = document.activeElement;
      const key = active && this.root.contains(active) ? active.dataset.key : null;
      const body = this.root.querySelector('.cdt-panel__body');
      const scroll = body ? body.scrollTop : 0;
      const step = this.ui.step;
      try {
        this.root.innerHTML = `<div class="cdt-shell${step >= 4 ? ' is-ws' : ''}${this.ui.collapsed ? ' is-collapsed' : ''}" data-step="${step}">${this.tRail()}<div class="cdt-stage" tabindex="-1">${this.tTop()}${step >= 4 ? this.tWorkspace() : this.tChoice()}</div></div>`;
      } finally {
        this.rendering = false;
      }
      const nb = this.root.querySelector('.cdt-panel__body');
      if (nb && scroll) nb.scrollTop = scroll;
      if (key) this.root.querySelector(`[data-key="${CSS.escape(key)}"]`)?.focus({ preventScroll: true });
      if (this.view() === '3d') this.draw3D();
      if (this.pending && !this.ptrDown) queueMicrotask(() => { if (this.pending) this.render(); });
    }

    tRail() {
      const labels = this.cfg.text.steps;
      const items = labels.map((l, i) => {
        const s = i + 1;
        const done = s < this.ui.step || (s <= this.ui.maxStep && s !== this.ui.step);
        const cur = s === this.ui.step;
        return `<li class="cdt-rail__item${cur ? ' is-current' : ''}${done ? ' is-done' : ''}"><button type="button" class="cdt-rail__dot" data-action="goto" data-step="${s}" data-key="rail-${s}" ${s > this.ui.maxStep ? 'disabled' : ''} ${cur ? 'aria-current="step"' : ''} aria-label="Step ${s}: ${esc(l)}">${done ? I.check : cur ? '<span class="cdt-rail__pulse"></span>' : s}</button><span class="cdt-rail__tip" aria-hidden="true">${esc(l)}</span></li>`;
      }).join('');
      return `<nav class="cdt-rail" aria-label="Design steps"><ol>${items}</ol><p class="cdt-rail__now"><span>Step ${this.ui.step} of 5</span> ${esc(labels[this.ui.step - 1])}</p></nav>`;
    }

    tTop() {
      const t = this.cfg.text;
      const hint = this.ui.step >= 4 ? `<p class="cdt-hint">${I.info}<span>${esc(this.ui.step === 4 ? t.steps[3] : t.steps[4])}</span></p>` : '<span></span>';
      const help = t.helpLabel ? `<a class="cdt-pill" href="${esc(t.helpLink || '#')}">${I.help}<span>${esc(t.helpLabel)}</span></a>` : '';
      return `<div class="cdt-top">${hint}<div class="cdt-top__actions">${help}<button type="button" class="cdt-pill" data-action="save" data-key="save">${I.save}<span>Save Design</span></button><button type="button" class="cdt-pill cdt-pill--icon" data-action="restart" data-key="restart" aria-label="Start over">${I.restart}</button></div></div>`;
    }

    tChoice() {
      const t = this.cfg.text;
      const d = this.design;
      let body = '';
      let back = '<span></span>';
      let nextLabel = '';
      if (this.ui.step === 1) {
        nextLabel = 'Continue to Layout Shape';
        body = `<p class="cdt-eyebrow">${esc(t.step1Eyebrow)}</p><h2 class="cdt-heading">${esc(t.step1Heading)}</h2><div class="cdt-lines" role="radiogroup" aria-label="Product line">${this.cfg.lines.map((l) => {
          const on = l.id === d.line;
          return `<button type="button" role="radio" aria-checked="${on}" class="cdt-line cdt-line--${esc(l.style || 'light')}${on ? ' is-on' : ''}" data-action="line" data-id="${esc(l.id)}" data-key="line-${esc(l.id)}"><span class="cdt-line__media">${l.image ? `<img src="${esc(l.image)}" alt="${esc(l.alt || l.name)}" loading="lazy" width="360" height="440">` : ''}${l.badge ? `<span class="cdt-line__badge">${esc(l.badge)}</span>` : ''}</span><span class="cdt-line__body"><span class="cdt-line__name">${esc(l.name)}</span>${l.subtitle ? `<span class="cdt-line__sub">${esc(l.subtitle)}</span>` : ''}${l.description ? `<span class="cdt-line__desc">${esc(l.description)}</span>` : ''}${l.features.length ? `<span class="cdt-line__rule"></span><span class="cdt-line__ft">Features</span>${l.features.map((f) => `<span class="cdt-line__feat"><span class="cdt-line__tick">${I.check}</span>${esc(f)}</span>`).join('')}` : ''}</span><span class="cdt-line__check">${I.check}</span></button>`;
        }).join('')}</div>`;
      } else if (this.ui.step === 2) {
        nextLabel = 'Continue to Door Type';
        back = `<button type="button" class="cdt-btn cdt-btn--primary" data-action="prev" data-key="prev">${I.back}<span>Change Product Line</span></button>`;
        body = `<h2 class="cdt-heading cdt-heading--sm">${esc(t.step2Heading)}</h2>${t.step2Note ? `<p class="cdt-note-accent">${esc(t.step2Note)}</p>` : ''}<div class="cdt-tiles" role="radiogroup" aria-label="Closet shape">${SHAPES.map((s) => {
          const on = s.id === d.shape;
          return `<button type="button" role="radio" aria-checked="${on}" class="cdt-tile${on ? ' is-on' : ''}" ${s.on ? `data-action="shape" data-id="${s.id}"` : 'aria-disabled="true"'} data-key="shape-${s.id}">${ico(s.d, '0 0 48 34')}<span>${esc(s.label)}</span>${s.on ? '' : '<em class="cdt-tile__tag">Coming soon</em>'}</button>`;
        }).join('')}</div>`;
      } else {
        nextLabel = 'Continue to Layout';
        back = `<button type="button" class="cdt-btn cdt-btn--primary" data-action="prev" data-key="prev">${I.back}<span>Change Layout Shape</span></button>`;
        body = `<h2 class="cdt-heading cdt-heading--sm">${esc(t.step3Heading)}</h2><div class="cdt-tiles cdt-tiles--doors" role="radiogroup" aria-label="Door type">${DOORS.map((s) => {
          const on = s.id === d.door.type;
          return `<button type="button" role="radio" aria-checked="${on}" class="cdt-tile${on ? ' is-on' : ''}" data-action="door-type" data-id="${s.id}" data-key="door-${s.id}">${ico(s.d, '0 0 48 32')}<span>${esc(s.label)}</span></button>`;
        }).join('')}</div>${t.step3Note ? `<p class="cdt-muted cdt-center">${esc(t.step3Note)}</p>` : ''}`;
      }
      return `<div class="cdt-choice"><div class="cdt-choice__inner">${body}</div></div><div class="cdt-bottom">${back}<button type="button" class="cdt-btn cdt-btn--primary" data-action="next" data-key="next"><span>${nextLabel}</span></button></div>`;
    }

    tWorkspace() {
      const step = this.ui.step;
      const tabs = step === 4 ? [['measure', 'Measurements'], ['arch', 'Architecture']] : [['prefs', 'Preferences'], ['modules', 'Modules'], ['acc', 'Accessories']];
      const tab = this.ui.tab[step];
      const panel = `<aside class="cdt-panel" aria-label="${step === 4 ? 'Layout design' : 'Closet design'} options"><div class="cdt-panel__head"><span class="cdt-panel__icon">${step === 4 ? I.layout : I.pencil}</span><h2>${step === 4 ? 'Layout Design' : 'Closet Design'}</h2><button type="button" class="cdt-icon-btn cdt-panel__collapse" data-action="collapse" data-key="collapse" aria-label="Hide panel">${I.collapse}</button></div><div class="cdt-tabs" role="tablist">${tabs.map(([id, l]) => `<button type="button" role="tab" id="cdt-tab-${id}" aria-selected="${tab === id}" aria-controls="cdt-tabpanel" class="cdt-tab${tab === id ? ' is-on' : ''}" data-action="tab" data-tab="${id}" data-key="tab-${id}">${l}</button>`).join('')}</div><div class="cdt-panel__body" role="tabpanel" id="cdt-tabpanel" aria-labelledby="cdt-tab-${tab}">${this.tPanel(tab)}</div></aside>`;
      const v = this.view();
      const val = step === 5 ? this.validate() : null;
      const status = step === 5
        ? (val.errors.length || val.warnings.length
          ? `<button type="button" class="cdt-status ${val.errors.length ? 'is-error' : 'is-warn'}" data-action="summary" data-key="status">${I.warn}<span>${val.errors.length ? `${val.errors.length} to fix` : ''}${val.errors.length && val.warnings.length ? ' · ' : ''}${val.warnings.length ? `${val.warnings.length} to check` : ''}</span></button>`
          : (this.design.modules.length ? `<span class="cdt-status is-ok">${I.check}<span>Fits your measurements</span></span>` : ''))
        : `<span class="cdt-status">${I.ruler}<span>${esc(SHAPES.find((s) => s.id === this.design.shape)?.label || '')} · ${this.len(this.design.room.width)} × ${this.len(this.design.room.depth)} · ${this.len(this.design.room.height)} H</span></span>`;
      const empty = step === 5 && !this.design.modules.length ? `<div class="cdt-empty"><p class="cdt-empty__title">Your closet is empty</p><p class="cdt-muted">Add modules one by one, or start from a suggested layout.</p><div class="cdt-empty__actions"><button type="button" class="cdt-btn cdt-btn--ghost" data-action="open-modules" data-key="empty-add">${I.plus}<span>Add Module</span></button><button type="button" class="cdt-btn cdt-btn--primary" data-action="auto" data-key="empty-auto">${I.spark}<span>Auto Populate</span></button></div></div>` : '';
      const label = v === 'plan' ? 'Closet floor plan' : v === 'wall' ? 'Wall elevation. Drag modules to move them, or select a module and use the arrow keys.' : '3D closet preview. Drag to rotate the view; click a module to select it.';
      const canvas = `<div class="cdt-canvas-wrap">${status}<div class="cdt-canvas-tools"><div class="cdt-unit" role="group" aria-label="Units"><button type="button" class="${this.ui.unit === 'in' ? 'is-on' : ''}" aria-pressed="${this.ui.unit === 'in'}" data-action="unit" data-unit="in" data-key="unit-in">in.</button><button type="button" class="${this.ui.unit === 'mm' ? 'is-on' : ''}" aria-pressed="${this.ui.unit === 'mm'}" data-action="unit" data-unit="mm" data-key="unit-mm">mm</button></div><button type="button" class="cdt-icon-btn" data-action="undo" data-key="undo" aria-label="Undo" ${this.past.length ? '' : 'disabled'}>${I.undo}</button><button type="button" class="cdt-icon-btn" data-action="redo" data-key="redo" aria-label="Redo" ${this.future.length ? '' : 'disabled'}>${I.redo}</button></div><div class="cdt-canvas cdt-canvas--${v}" data-canvas tabindex="0" role="application" aria-label="${esc(label)}" data-key="canvas">${this.tCanvas()}</div>${empty}</div>`;
      const open = this.ui.collapsed ? `<button type="button" class="cdt-icon-btn cdt-panel-open" data-action="expand" data-key="expand" aria-label="Show panel">${I.expand}</button>` : '';
      return `<div class="cdt-ws">${panel}${open}${canvas}</div>${this.tBottom()}`;
    }

    tBottom() {
      const step = this.ui.step;
      const v = this.view();
      const views = [['plan', 'Floor plan', I.plan], ['wall', '2D wall view', I.wall], ['3d', '3D view', I.cube]];
      const viewBtns = `<div class="cdt-seg" role="group" aria-label="View">${views.map(([id, l, ic]) => `<button type="button" class="${v === id ? 'is-on' : ''}" aria-pressed="${v === id}" aria-label="${l}" title="${l}" data-action="view" data-view="${id}" data-key="view-${id}">${ic}</button>`).join('')}</div>`;
      const cam = v === '3d' ? `<label class="cdt-cam"><span class="cdt-sr">Camera</span><select data-select="cam" data-key="cam">${Object.keys(CAMS).map((k) => `<option value="${k}" ${this.ui.cam === k ? 'selected' : ''}>${k[0].toUpperCase() + k.slice(1)} View</option>`).join('')}${this.ui.cam === 'custom' ? '<option value="custom" selected>Custom View</option>' : ''}</select></label>` : '';
      if (step === 4) {
        return `<div class="cdt-bottom cdt-bottom--ws"><button type="button" class="cdt-btn cdt-btn--primary" data-action="prev" data-key="prev">${I.back}<span>Change Layout</span></button><div class="cdt-toolbar">${viewBtns}${cam}</div><button type="button" class="cdt-btn cdt-btn--primary" data-action="next" data-key="next"><span>Continue to Design</span></button></div>`;
      }
      const b = this.bom();
      const has = this.design.modules.length > 0;
      const tool = has ? `<button type="button" class="cdt-btn cdt-btn--muted" data-action="clear" data-key="clear">Clear Modules</button>` : `<button type="button" class="cdt-btn cdt-btn--muted" data-action="auto" data-key="auto">Auto Populate</button>`;
      return `<div class="cdt-bottom cdt-bottom--ws"><button type="button" class="cdt-btn cdt-btn--primary" data-action="prev" data-key="prev">${I.back}<span>Edit Layout</span></button><div class="cdt-toolbar">${tool}${viewBtns}${cam}</div><div class="cdt-price"><p><span class="cdt-price__label">Price:</span> <strong>${this.money.format(b.total / 100)}</strong> <span class="cdt-price__tag">Sample</span></p><button type="button" class="cdt-link" data-action="summary" data-key="view-summary">View</button><button type="button" class="cdt-btn cdt-btn--primary" data-action="cart" data-key="cart" ${has ? '' : 'aria-disabled="true"'}>${I.cart}<span>Add to Cart</span></button></div></div>`;
    }

    field(name, labelText, u, opts = {}) {
      const id = `cdt-f-${name.replace(/[^a-z0-9]/gi, '-')}`;
      return `<div class="cdt-field"><label for="${id}">${esc(labelText)}</label><span class="cdt-field__box"><input id="${id}" type="text" inputmode="decimal" autocomplete="off" data-field="${esc(name)}" data-key="${id}" value="${esc(this.val(u))}" aria-describedby="${id}-err"${opts.hint ? ` aria-description="${esc(opts.hint)}"` : ''}><span class="cdt-field__unit">${this.ui.unit === 'mm' ? 'mm' : 'in'}</span></span><span class="cdt-field__err" id="${id}-err"></span></div>`;
    }

    tPanel(tab) {
      const d = this.design;
      if (tab === 'measure') {
        const doorLabel = DOORS.find((x) => x.id === d.door.type)?.label || '';
        return `<section class="cdt-card"><h3>${I.ruler}Room size</h3><p class="cdt-muted">Measure wall to wall at the widest point. You can type fractions, e.g. 29 1/2.</p>${this.field('room.width', 'Wall B · back wall width', d.room.width)}${this.field('room.depth', 'Wall A / Wall C · closet depth', d.room.depth)}${this.field('room.height', 'Wall height', d.room.height)}</section><section class="cdt-card"><h3>${I.door}Door opening</h3><div class="cdt-row"><span class="cdt-chip">${esc(doorLabel)}</span><button type="button" class="cdt-link" data-action="goto" data-step="3" data-key="change-door">Change Door</button></div>${this.field('door.width', 'Opening width', d.door.width)}${this.field('door.offset', 'Opening position from left wall', this.span().a)}<button type="button" class="cdt-btn cdt-btn--ghost cdt-btn--sm" data-action="door-center" data-key="door-center" ${d.door.offset == null ? 'disabled' : ''}>Center the opening</button></section>`;
      }
      if (tab === 'arch') {
        const feats = d.features.map((f) => {
          const F = FEATURES[f.type];
          const on = this.ui.selFeat === f.id;
          return `<div class="cdt-feat${on ? ' is-on' : ''}"><div class="cdt-row"><button type="button" class="cdt-feat__name" data-action="select-feat" data-id="${f.id}" data-key="feat-${f.id}">${I[f.type]}<span>${F.label}</span></button><button type="button" class="cdt-icon-btn" data-action="remove-feat" data-id="${f.id}" data-key="feat-rm-${f.id}" aria-label="Remove ${F.label}">${I.trash}</button></div><div class="cdt-grid2">${this.field(`feat.x:${f.id}`, 'From left', f.x)}${this.field(`feat.y:${f.id}`, 'From floor', f.y)}</div></div>`;
        }).join('');
        const add = (types) => types.map((tp) => `<button type="button" class="cdt-add" data-action="add-feat" data-type="${tp}" data-key="add-${tp}">${I[tp]}<span>${FEATURES[tp].label}</span></button>`).join('');
        return `<section class="cdt-card"><h3>${I.door}Add Opening</h3><p class="cdt-muted">Add doors and windows to your layout</p><div class="cdt-adds"><button type="button" class="cdt-add" data-action="goto" data-step="3" data-key="add-door">${I.door}<span>Door</span></button>${add(['window'])}</div></section><section class="cdt-card"><h3>${I.outlet}Add Wall Decorators</h3><p class="cdt-muted">Mark switches, outlets and vents on Wall B so the tool can warn you if a module covers them.</p><div class="cdt-adds cdt-adds--3">${add(['switch', 'outlet', 'vent'])}</div>${feats ? `<div class="cdt-feats">${feats}</div>` : ''}</section><section class="cdt-card"><h3>${I.wall}Wall</h3><p class="cdt-muted">Customize wall height and color</p>${this.field('room.height', 'Height', d.room.height)}<div class="cdt-field"><label for="cdt-wall-color">Color</label><span class="cdt-color"><input type="color" id="cdt-wall-color" value="${esc(d.room.color)}" data-color="room" data-key="wall-color"><code>${esc(d.room.color.toUpperCase())}</code><button type="button" class="cdt-btn cdt-btn--ghost cdt-btn--sm" data-action="wall-reset" data-key="wall-reset">Reset Color</button></span></div><p class="cdt-muted cdt-small">Wall color is for preview only. It does not change your component list or price.</p></section>`;
      }
      if (tab === 'prefs') {
        const p = d.prefs;
        const sw = (key, arr) => `<div class="cdt-swatches" role="radiogroup" aria-label="${key === 'color' ? 'Closet color' : 'Handle finish'}">${arr.map((c) => `<button type="button" role="radio" aria-checked="${p[key] === c.name}" class="cdt-swatch${p[key] === c.name ? ' is-on' : ''}" data-action="pref" data-pref="${key}" data-value="${esc(c.name)}" data-key="pref-${key}-${esc(c.name)}"><span style="background:${esc(c.hex)}"></span>${esc(c.name)}</button>`).join('')}</div>`;
        const radios = (key, arr, lbl) => `<div class="cdt-seg cdt-seg--text" role="radiogroup" aria-label="${lbl}">${arr.map((v) => `<button type="button" role="radio" aria-checked="${p[key] === v}" class="${p[key] === v ? 'is-on' : ''}" data-action="pref" data-pref="${key}" data-value="${esc(v)}" data-key="pref-${key}-${esc(v)}">${esc(v)}</button>`).join('')}</div>`;
        const sel = (key, arr, lbl) => `<div class="cdt-field"><label for="cdt-pref-${key}">${lbl}</label><select id="cdt-pref-${key}" data-select="pref.${key}" data-key="pref-${key}">${arr.map((o) => `<option value="${o.value}" ${p[key] === o.value ? 'selected' : ''}>${esc(o.label)}</option>`).join('')}</select></div>`;
        return `<p class="cdt-info">${I.info}<span>Design preferences will be applied to all modules</span></p><section class="cdt-card"><h3>${I.wall}Closet</h3><p class="cdt-label">Color</p>${sw('color', this.cfg.colors)}${sel('depth', this.cfg.depths.map((o) => ({ value: o.value, label: `${this.len(o.value)}${o.label ? ` · ${o.label}` : ''}` })), 'Depth')}${sel('height', this.cfg.heights.map((h) => ({ value: h, label: this.len(h) })), 'Height')}</section><section class="cdt-card"><h3>${I.vent}Drawer</h3><p class="cdt-label">Type</p>${radios('drawer', this.cfg.drawerStyles, 'Drawer type')}</section><section class="cdt-card"><h3>${I.door}Door</h3><p class="cdt-label">Type</p>${radios('door', this.cfg.doorStyles, 'Door type')}</section><section class="cdt-card"><h3>${I.minus}Handle</h3>${sw('handle', this.cfg.handles)}</section>`;
      }
      if (tab === 'modules') {
        const sel = d.modules.find((m) => m.id === this.ui.sel);
        const val = this.validate();
        let insp = '';
        if (sel) {
          const def = this.def(sel.type);
          const issues = (val.by[sel.id] || []).map((x) => `<li class="is-${x.level}">${x.level === 'error' ? I.warn : I.info}<span>${esc(x.msg)}</span></li>`).join('');
          insp = `<section class="cdt-card cdt-insp"><div class="cdt-row"><h3>${esc(this.label(sel))}</h3><button type="button" class="cdt-icon-btn" data-action="deselect" data-key="deselect" aria-label="Close module settings">${I.close}</button></div><p class="cdt-muted">${this.len(sel.width)} W × ${this.len(this.mH(sel))} H × ${this.len(this.mD())} D · ${this.money.format(this.price(sel) / 100)} <span class="cdt-price__tag">Sample</span></p>${issues ? `<ul class="cdt-issues">${issues}</ul>` : ''}<div class="cdt-field"><label for="cdt-mod-width">Width</label><select id="cdt-mod-width" data-select="mod.width" data-key="mod-width">${def.widths.map((w) => `<option value="${toU(w)}" ${toU(w) === sel.width ? 'selected' : ''}>${this.len(toU(w))}</option>`).join('')}</select></div>${this.field('mod.x', 'Position from left wall', sel.x)}<div class="cdt-row cdt-row--btns"><button type="button" class="cdt-icon-btn" data-action="move" data-dir="-1" data-key="move-l" aria-label="Move left 1 inch">${I.left}</button><button type="button" class="cdt-icon-btn" data-action="move" data-dir="1" data-key="move-r" aria-label="Move right 1 inch">${I.right}</button><button type="button" class="cdt-btn cdt-btn--ghost cdt-btn--sm" data-action="dup" data-key="dup">${I.copy}<span>Duplicate</span></button><button type="button" class="cdt-btn cdt-btn--ghost cdt-btn--sm cdt-btn--danger" data-action="remove" data-key="remove">${I.trash}<span>Remove</span></button></div><p class="cdt-muted cdt-small">Tip: in the 2D wall view you can drag modules, or use ← → (Shift for 6").</p></section>`;
        }
        const c = this.colors();
        const cards = this.available().map((m) => {
          const w = m.widths[0];
          const h = m.fixedHeight > 0 ? m.fixedHeight : inch(d.prefs.height);
          const thumb = m.image ? `<img src="${esc(m.image)}" alt="" loading="lazy" width="120" height="120">` : `<svg viewBox="-1 -1 ${n(w + 2)} ${n(inch(d.prefs.height) + 2)}" preserveAspectRatio="xMidYMax meet" aria-hidden="true">${moduleSVG(m.pattern, w, h, 0, (y) => inch(d.prefs.height) - y, c)}</svg>`;
          const from = m.prices.length ? Math.min(...m.prices) : 0;
          return `<button type="button" class="cdt-mcard" data-action="add-module" data-id="${esc(m.id)}" data-key="add-${esc(m.id)}"><span class="cdt-mcard__img">${thumb}</span><span class="cdt-mcard__name">${esc(m.title)}</span><span class="cdt-mcard__meta">${m.widths.map((x) => this.len(toU(x))).join(' / ')} W</span>${from ? `<span class="cdt-mcard__price">from ${this.money.format(from / 100)}</span>` : ''}<span class="cdt-mcard__add" aria-hidden="true">${I.plus}</span></button>`;
        }).join('');
        const placed = this.sorted().map((m, i) => {
          const issues = val.by[m.id] || [];
          const lvl = issues.some((x) => x.level === 'error') ? 'error' : issues.length ? 'warn' : '';
          return `<li><button type="button" class="cdt-placed${m.id === this.ui.sel ? ' is-on' : ''}" data-action="select" data-inst="${m.id}" data-key="placed-${m.id}"><span class="cdt-placed__n">${i + 1}</span><span>${esc(this.def(m.type)?.title || '')}<small>${this.len(m.width)} W · ${this.len(m.x)} from left</small></span>${lvl ? `<span class="cdt-placed__flag is-${lvl}" title="${lvl === 'error' ? 'Needs fixing' : 'Please check'}">${I.warn}<span class="cdt-sr">${lvl === 'error' ? 'Needs fixing' : 'Please check'}</span></span>` : ''}</button></li>`;
        }).join('');
        return `${insp}<section class="cdt-card"><h3>${I.plus}Add Module</h3><p class="cdt-muted">Click a module to add it to the first free space on Wall B.</p><div class="cdt-mcards">${cards || '<p class="cdt-muted">No modules are set up for this product line yet.</p>'}</div></section>${placed ? `<section class="cdt-card"><h3>${I.wall}In your design</h3><ol class="cdt-placed-list">${placed}</ol></section>` : ''}`;
      }
      // accessories
      const b = this.bom();
      const accs = this.cfg.accessories.map((a) => {
        const q = d.acc[a.id] || 0;
        return `<div class="cdt-acc"><div><p class="cdt-acc__name">${esc(a.title)}</p>${a.description ? `<p class="cdt-muted cdt-small">${esc(a.description)}</p>` : ''}<p class="cdt-small">${this.money.format(a.price / 100)} <span class="cdt-price__tag">Sample</span></p></div><div class="cdt-qty" role="group" aria-label="${esc(a.title)} quantity"><button type="button" class="cdt-icon-btn" data-action="acc" data-id="${esc(a.id)}" data-delta="-1" data-key="acc-m-${esc(a.id)}" aria-label="Remove one" ${q ? '' : 'disabled'}>${I.minus}</button><output aria-live="polite">${q}</output><button type="button" class="cdt-icon-btn" data-action="acc" data-id="${esc(a.id)}" data-delta="1" data-key="acc-p-${esc(a.id)}" aria-label="Add one" ${q >= a.max ? 'disabled' : ''}>${I.plus}</button></div></div>`;
      }).join('');
      return `<section class="cdt-card"><h3>${I.check}Required parts</h3>${b.handles ? `<div class="cdt-acc"><div><p class="cdt-acc__name">Handle · ${esc(d.prefs.handle)}</p><p class="cdt-muted cdt-small">Added automatically: 1 per drawer and door front in your design.</p></div><strong class="cdt-acc__qty">× ${b.handles}</strong></div>` : '<p class="cdt-muted">Handles are added automatically when your design has drawers or doors.</p>'}</section><section class="cdt-card"><h3>${I.plus}Optional accessories</h3>${accs || '<p class="cdt-muted">No accessories are set up yet.</p>'}</section>`;
    }

    textScale() {
      const w = this.root.querySelector('[data-canvas]')?.clientWidth || this.root.clientWidth || 900;
      return clamp(700 / w, 1, 1.6);
    }

    tCanvas() {
      const v = this.view();
      if (v === 'plan') return this.svgPlan();
      if (v === 'wall') return this.svgWall();
      return '<div class="cdt3" data-3d></div>';
    }

    svgWall() {
      const d = this.design;
      const W = inch(d.room.width);
      const H = inch(d.room.height);
      const m = Math.max(W, H);
      const k = this.textScale();
      const pad = m * 0.09 * k;
      const fs = clamp(m / 34, 1.6, 9) * k;
      const sy = (y) => H - y;
      const c = this.colors();
      const val = this.validate();
      const { a, b } = this.span();
      const A = inch(a);
      const B = inch(b);
      let o = `<defs><pattern id="cdt-hatch" width="3" height="3" patternUnits="userSpaceOnUse" patternTransform="rotate(45)"><rect width="3" height="3" fill="rgba(29,29,31,.05)"/><path d="M0 0V3" stroke="rgba(29,29,31,.18)" stroke-width="1" vector-effect="non-scaling-stroke"/></pattern></defs>`;
      o += `<rect x="0" y="0" width="${n(W)}" height="${n(H)}" fill="${esc(d.room.color)}"/>`;
      o += `<rect x="${n(-pad * 0.3)}" y="${n(H)}" width="${n(W + pad * 0.6)}" height="${n(fs * 0.9)}" fill="#d4b08a"/>`;
      if (d.shape === 'reach-in') {
        if (A > 0) o += `<rect x="0" y="0" width="${n(A)}" height="${n(H)}" fill="url(#cdt-hatch)"/>`;
        if (B < W) o += `<rect x="${n(B)}" y="0" width="${n(W - B)}" height="${n(H)}" fill="url(#cdt-hatch)"/>`;
        o += `<text class="cdt-svg-note" x="${n((A + B) / 2)}" y="${n(fs * 1.4)}" font-size="${n(fs * 0.7)}" text-anchor="middle">Door opening ${this.len(b - a)}</text>`;
      }
      d.features.forEach((f) => {
        const F = FEATURES[f.type];
        const on = this.ui.selFeat === f.id;
        o += `<g class="cdt-feat-g${on ? ' is-on' : ''}" data-feat="${f.id}">${featureSVG(f.type, inch(f.x), inch(f.y), F.w, F.h, sy)}${on ? `<rect x="${n(inch(f.x) - 0.8)}" y="${n(sy(inch(f.y) + F.h) - 0.8)}" width="${n(F.w + 1.6)}" height="${n(F.h + 1.6)}" class="cdt-sel-ring"/>` : ''}</g>`;
      });
      this.sorted().forEach((mm) => {
        const def = this.def(mm.type);
        if (!def) return;
        const w = inch(mm.width);
        const h = inch(this.mH(mm));
        const x = inch(mm.x);
        const issues = val.by[mm.id] || [];
        const bad = issues.some((i) => i.level === 'error');
        const on = mm.id === this.ui.sel;
        o += `<g class="cdt-mod${on ? ' is-on' : ''}${bad ? ' is-bad' : ''}" data-inst="${mm.id}">${moduleSVG(def.pattern, w, h, x, sy, c)}<rect class="cdt-hit" x="${n(x)}" y="${n(sy(h))}" width="${n(w)}" height="${n(h)}"/>${on || bad ? `<rect class="cdt-sel-ring${bad ? ' is-bad' : ''}" x="${n(x - 0.6)}" y="${n(sy(h) - 0.6)}" width="${n(w + 1.2)}" height="${n(h + 1.2)}"/>` : ''}${issues.length ? `<g class="cdt-badge${bad ? ' is-bad' : ''}"><circle cx="${n(x + w - fs * 0.6)}" cy="${n(sy(h) + fs * 0.6)}" r="${n(fs * 0.5)}"/><text x="${n(x + w - fs * 0.6)}" y="${n(sy(h) + fs * 0.82)}" font-size="${n(fs * 0.7)}" text-anchor="middle">!</text></g>` : ''}</g>`;
        o += `<g class="cdt-dimline"><path d="M${n(x)} ${n(H + pad * 0.45)}H${n(x + w)}M${n(x)} ${n(H + pad * 0.3)}V${n(H + pad * 0.6)}M${n(x + w)} ${n(H + pad * 0.3)}V${n(H + pad * 0.6)}"/><text x="${n(x + w / 2)}" y="${n(H + pad * 0.45 + fs * 1.1)}" font-size="${n(fs * 0.75)}" text-anchor="middle">${this.len(mm.width)}</text></g>`;
      });
      o += `<g class="cdt-dimline cdt-dimline--main"><path d="M0 ${n(-pad * 0.45)}H${n(W)}M0 ${n(-pad * 0.65)}V${n(-pad * 0.25)}M${n(W)} ${n(-pad * 0.65)}V${n(-pad * 0.25)}"/><text x="${n(W / 2)}" y="${n(-pad * 0.6)}" font-size="${n(fs)}" text-anchor="middle">Wall B · ${this.len(d.room.width)}</text><path d="M${n(-pad * 0.45)} 0V${n(H)}M${n(-pad * 0.65)} 0H${n(-pad * 0.25)}M${n(-pad * 0.65)} ${n(H)}H${n(-pad * 0.25)}"/><text x="${n(-pad * 0.6)}" y="${n(H / 2)}" font-size="${n(fs)}" text-anchor="middle" transform="rotate(-90 ${n(-pad * 0.6)} ${n(H / 2)})">${this.len(d.room.height)}</text></g>`;
      return `<svg class="cdt-svg" data-svg viewBox="${n(-pad * 1.2)} ${n(-pad * 1.15)} ${n(W + pad * 2.4)} ${n(H + pad * 2.1)}" preserveAspectRatio="xMidYMid meet">${o}</svg>`;
    }

    svgPlan() {
      const d = this.design;
      const W = inch(d.room.width);
      const D = inch(d.room.depth);
      const m = Math.max(W, D);
      const wt = clamp(Math.min(W, D) * 0.07, 2.5, 6);
      const k = this.textScale();
      const pad = m * 0.24 * Math.sqrt(k);
      const fs = clamp(m / 30, 1.6, 10) * k;
      const { a, b } = this.span();
      const A = inch(a);
      const B = inch(b);
      const dw = B - A;
      const yw = D + wt;
      const val = this.ui.step === 5 ? this.validate() : { by: {} };
      let o = `<rect x="0" y="0" width="${n(W)}" height="${n(D)}" fill="#ffffff"/>`;
      this.sorted().forEach((mm) => {
        const x = inch(mm.x);
        const w = inch(mm.width);
        const dd = Math.min(inch(this.mD()), D + wt);
        const bad = (val.by[mm.id] || []).some((i) => i.level === 'error');
        const on = mm.id === this.ui.sel;
        o += `<g class="cdt-mod cdt-mod--plan${on ? ' is-on' : ''}${bad ? ' is-bad' : ''}" data-inst="${mm.id}"><rect x="${n(x)}" y="0" width="${n(w)}" height="${n(dd)}" fill="${esc(this.colors().base)}" stroke="${esc(this.colors().edge)}" stroke-width="1" vector-effect="non-scaling-stroke"/><rect class="cdt-hit" x="${n(x)}" y="0" width="${n(w)}" height="${n(dd)}"/>${on || bad ? `<rect class="cdt-sel-ring${bad ? ' is-bad' : ''}" x="${n(x)}" y="0" width="${n(w)}" height="${n(dd)}"/>` : ''}<text x="${n(x + w / 2)}" y="${n(dd / 2 + fs * 0.3)}" font-size="${n(fs * 0.62)}" text-anchor="middle" class="cdt-svg-note">${this.len(mm.width)}</text></g>`;
      });
      const wall = (x, y, w, h) => (w > 0 && h > 0 ? `<rect class="cdt-wallfill" x="${n(x)}" y="${n(y)}" width="${n(w)}" height="${n(h)}"/>` : '');
      o += wall(-wt, -wt, W + 2 * wt, wt) + wall(-wt, 0, wt, D + wt) + wall(W, 0, wt, D + wt);
      if (d.shape === 'reach-in') o += wall(0, D, A, wt) + wall(B, D, W - B, wt);
      o += `<rect x="${n(-wt)}" y="${n(D + wt)}" width="${n(W + 2 * wt)}" height="${n(wt * 0.3)}" fill="#dad8d0"/>`;
      const st = 'class="cdt-door" vector-effect="non-scaling-stroke"';
      const t = d.door.type;
      if (t === 'single') o += `<path ${st} d="M${n(A)} ${n(yw)}V${n(yw + dw)}M${n(B)} ${n(yw)}A${n(dw)} ${n(dw)} 0 0 1 ${n(A)} ${n(yw + dw)}"/>`;
      else if (t === 'double') o += `<path ${st} d="M${n(A)} ${n(yw)}V${n(yw + dw / 2)}M${n(A + dw / 2)} ${n(yw)}A${n(dw / 2)} ${n(dw / 2)} 0 0 1 ${n(A)} ${n(yw + dw / 2)}M${n(B)} ${n(yw)}V${n(yw + dw / 2)}M${n(A + dw / 2)} ${n(yw)}A${n(dw / 2)} ${n(dw / 2)} 0 0 0 ${n(B)} ${n(yw + dw / 2)}"/>`;
      else if (t === 'sliding') o += `<rect ${st} x="${n(A)}" y="${n(D + wt * 0.1)}" width="${n(dw * 0.55)}" height="${n(wt * 0.35)}"/><rect ${st} x="${n(B - dw * 0.55)}" y="${n(D + wt * 0.55)}" width="${n(dw * 0.55)}" height="${n(wt * 0.35)}"/>`;
      else if (t === 'pocket') o += `<rect ${st} stroke-dasharray="4 3" x="${n(B)}" y="${n(D + wt * 0.35)}" width="${n(Math.min(dw, W - B + wt))}" height="${n(wt * 0.3)}"/>`;
      else if (t === 'bifold') { const q = dw / 4; o += `<path ${st} d="M${n(A)} ${n(yw)}L${n(A + q / 2)} ${n(yw + q)}L${n(A + q)} ${n(yw)}M${n(B)} ${n(yw)}L${n(B - q / 2)} ${n(yw + q)}L${n(B - q)} ${n(yw)}"/>`; }
      if (d.shape === 'reach-in') o += `<path ${st} d="M${n(A)} ${n(D)}v${n(wt)}M${n(B)} ${n(D)}v${n(wt)}"/>`;
      const tag = (x, y, lx, ly, title, value, focus) => `<g class="cdt-tag" data-action="focus-field" data-target="${focus}"><path d="M${n(x)} ${n(y)}L${n(lx)} ${n(ly)}"/><circle cx="${n(x)}" cy="${n(y)}" r="${n(fs * 0.28)}"/>${title ? `<text class="cdt-tag__title" x="${n(lx)}" y="${n(ly - fs * 1.35)}" font-size="${n(fs * 0.9)}" text-anchor="middle">${title}</text>` : ''}<rect x="${n(lx - fs * 2.3)}" y="${n(ly - fs * 0.85)}" width="${n(fs * 4.6)}" height="${n(fs * 1.7)}" rx="${n(fs * 0.15)}"/><text x="${n(lx)}" y="${n(ly + fs * 0.3)}" font-size="${n(fs * 0.8)}" text-anchor="middle">${value}</text></g>`;
      o += tag(W / 2, -wt, W / 2, -pad * 0.55, 'Wall B', this.len(d.room.width), 'room.width');
      o += tag(-wt, D / 2, -pad * 0.55, D / 2, 'Wall A', this.len(d.room.depth), 'room.depth');
      o += tag(W + wt, D / 2, W + pad * 0.55, D / 2, 'Wall C', this.len(d.room.depth), 'room.depth');
      const swing = { single: dw, double: dw / 2, bifold: dw / 4 }[t] || 0;
      const tagY = yw + Math.max(swing, pad * 0.3) + fs * 1.6;
      if (d.shape === 'reach-in') o += tag((A + B) / 2, D + wt / 2, (A + B) / 2, tagY, '', this.len(b - a), 'door.width');
      [[0, 0, 1], [W, 0, -1]].forEach(([x, y, dir]) => {
        const r = fs * 1.6;
        o += `<path class="cdt-angle" d="M${n(x + dir * r)} ${n(y)}A${n(r)} ${n(r)} 0 0 ${dir > 0 ? 1 : 0} ${n(x)} ${n(y + r)}"/><text class="cdt-svg-note" x="${n(x + dir * r * 1.15)}" y="${n(y + r * 1.25)}" font-size="${n(fs * 0.6)}" text-anchor="middle">90°</text>`;
      });
      return `<svg class="cdt-svg" data-svg viewBox="${n(-pad)} ${n(-pad)} ${n(W + 2 * pad)} ${n(tagY + fs * 1.4 + pad)}" preserveAspectRatio="xMidYMid meet">${o}</svg>`;
    }

    draw3D() {
      const host = this.root.querySelector('[data-3d]');
      if (!host) return;
      const cw = host.clientWidth;
      const ch = host.clientHeight;
      if (!cw || !ch) return;
      const d = this.design;
      const W = inch(d.room.width);
      const H = inch(d.room.height);
      const D = inch(d.room.depth);
      const s = Math.min((cw * 0.7) / W, (ch * 0.74) / H);
      const ctx = { W, H, D, s };
      const P = Math.max(W, H) * s * 2.3;
      host.style.perspective = `${n(P)}px`;
      const wall = d.room.color;
      let o = face('cdt3-wall', W * s, H * s, 0, 0, (-D * s) / 2, '', wall);
      o += face('cdt3-wall cdt3-side', D * s, H * s, (-W * s) / 2, 0, 0, 'rotateY(90deg)', shade(wall, -0.14));
      o += face('cdt3-wall cdt3-side', D * s, H * s, (W * s) / 2, 0, 0, 'rotateY(-90deg)', shade(wall, -0.14));
      o += face('cdt3-floor', W * s, D * s, 0, (H * s) / 2, 0, 'rotateX(90deg)', '');
      o += face('cdt3-ceil', W * s, D * s, 0, (-H * s) / 2, 0, 'rotateX(-90deg)', '#f4f3ee');
      o += face('cdt3-light', Math.min(W * 0.8, 60) * s, Math.max(6, 2.2 * s), 0, (-H * s) / 2 + 2, (-D * s) / 2 + Math.min(D * 0.45, 12) * s, 'rotateX(-90deg)', '#ffffff');
      o += face('cdt3-tag', 120, 24, 0, (-H * s) / 2 + 30, (-D * s) / 2 + 1, '', '', 'aria-hidden="true"').replace('></i>', '>Wall B</i>');
      d.features.forEach((f) => {
        const F = FEATURES[f.type];
        o += box(ctx, inch(f.x), inch(f.y), 0, F.w, F.h, 0.3, f.type === 'window' ? '#f4f3ee' : '#f4f3ee', 'f', `cdt3-feat cdt3-feat--${f.type}`);
      });
      const c = this.colors();
      const val = this.validate();
      const dd = inch(this.mD());
      this.design.modules.forEach((m) => {
        const def = this.def(m.type);
        if (!def) return;
        const w = inch(m.width);
        const h = inch(this.mH(m));
        const x0 = inch(m.x);
        const p = parts(def.pattern, h);
        const bad = (val.by[m.id] || []).some((i) => i.level === 'error');
        const cls = `cdt3-m${m.id === this.ui.sel ? ' is-sel' : ''}${bad ? ' is-bad' : ''}`;
        const at = `data-inst="${m.id}"`;
        const iw = w - 2 * T;
        const ix = x0 + T;
        const col = c.base;
        o += box(ctx, ix, PL, 0, iw, h - PL - T, 0.25, c.inner, 'f', cls, at);
        o += box(ctx, x0, 0, 0, T, h, dd, col, 'flrt', cls, at);
        o += box(ctx, x0 + w - T, 0, 0, T, h, dd, col, 'flrt', cls, at);
        o += box(ctx, ix, h - T, 0, iw, T, dd, col, 'ftb', cls, at);
        o += box(ctx, ix, PL - T, 0, iw, T, dd, col, 'ft', cls, at);
        o += box(ctx, ix + 0.4, 0, 0, iw - 0.8, PL - T, dd - 1, c.dark, 'f', cls, at);
        p.shelves.forEach((y) => { o += box(ctx, ix, y, 0.25, iw, T, dd - 0.75, col, 'ftb', cls, at); });
        p.fences.forEach((y) => { o += box(ctx, ix, y + T, dd - 1.5, iw, 1.2, 0.3, shade(col, -0.05), 'f', cls, at); });
        if (p.bench != null) o += box(ctx, ix, p.bench - 1.5, 0.25, iw, 1.5, dd, col, 'ftb', cls, at);
        p.rods.forEach((y) => { o += box(ctx, ix, y, dd / 2 - 0.5, iw, 1, 1, '#b4b4b1', 'fb', cls, at); });
        p.hooks.forEach((y) => { [0.25, 0.5, 0.75].forEach((t) => { o += box(ctx, ix + iw * t - 0.5, y, 0.25, 1, 1, 2.5, c.handle, 'ft', cls, at); }); });
        const hand = (x, y, hw, hh) => box(ctx, x, y, dd, hw, hh, 0.6, c.handle, 'ft', cls, at);
        p.drawers.forEach((dr) => {
          o += box(ctx, ix + 0.15, dr.y + 0.15, dd - 0.75, iw - 0.3, dr.h - 0.3, 0.75, col, 'ft', `${cls}${c.drawer === 'Shaker' ? ' is-shaker' : ''}`, at);
          const hl = Math.min(6, iw * 0.35);
          o += hand(ix + iw / 2 - hl / 2, dr.y + dr.h - 2.6, hl, 0.7);
        });
        p.doors.forEach((dr) => {
          const k = leaves(w);
          const lw = iw / k;
          for (let i = 0; i < k; i += 1) {
            const dx = ix + i * lw;
            o += box(ctx, dx + 0.15, dr.y, dd - 0.75, lw - 0.3, dr.h - 0.15, 0.75, col, 'ft', `${cls}${c.door === 'Shaker' ? ' is-shaker' : ''}`, at);
            o += hand(k === 2 && i === 1 ? dx + 1.4 : dx + lw - 2.1, dr.y + 2, 0.7, 5);
          }
        });
      });
      host.innerHTML = `<div class="cdt3-world" data-world>${o}</div>`;
      this.applyCam();
    }

    applyCam() {
      const w = this.root.querySelector('[data-world]');
      if (w) w.style.transform = `rotateX(${n(this.ui.pitch)}deg) rotateY(${n(this.ui.yaw)}deg)`;
    }

    /* ---------- dialogs & toast ---------- */
    toast(msg, tone = '') {
      if (!this.toastEl) return;
      this.toastEl.textContent = msg;
      this.toastEl.className = `cdt-toast is-show${tone ? ` is-${tone}` : ''}`;
      clearTimeout(this.toastTimer);
      this.toastTimer = setTimeout(() => { this.toastEl.className = 'cdt-toast'; }, 4200);
    }

    openDialog({ title, html, actions = [], wide = false, cls = '' }) {
      this.closeDialog(false);
      this.lastFocus = document.activeElement;
      const wrap = document.createElement('div');
      wrap.className = `cdt-modal${cls ? ` ${cls}` : ''}`;
      wrap.innerHTML = `<div class="cdt-dialog${wide ? ' cdt-dialog--wide' : ''}" role="dialog" aria-modal="true" aria-labelledby="cdt-dlg-title"><button type="button" class="cdt-icon-btn cdt-dialog__x" data-dlg="close" aria-label="Close">${I.close}</button><h2 id="cdt-dlg-title" class="cdt-dialog__title">${esc(title)}</h2><div class="cdt-dialog__body">${html}</div>${actions.length ? `<div class="cdt-dialog__actions">${actions.map((a, i) => (a.href ? `<a class="cdt-btn cdt-btn--${a.variant || 'ghost'}" href="${esc(a.href)}">${esc(a.label)}</a>` : `<button type="button" class="cdt-btn cdt-btn--${a.variant || 'ghost'}" data-dlg="${i}">${esc(a.label)}</button>`)).join('')}</div>` : ''}</div>`;
      wrap.addEventListener('click', (e) => {
        if (e.target === wrap) { this.closeDialog(); return; }
        const b = e.target.closest('[data-dlg]');
        if (!b) return;
        if (b.dataset.dlg === 'close') { this.closeDialog(); return; }
        const a = actions[+b.dataset.dlg];
        if (a?.onClick) { const keep = a.onClick(wrap); if (keep === true) return; }
        this.closeDialog(!a?.noRefocus);
      });
      wrap.addEventListener('keydown', (e) => {
        if (e.key === 'Escape') { e.preventDefault(); this.closeDialog(); }
        if (e.key !== 'Tab') return;
        const f = [...wrap.querySelectorAll('button:not([disabled]), a[href], input, select')];
        if (!f.length) return;
        if (e.shiftKey && document.activeElement === f[0]) { e.preventDefault(); f[f.length - 1].focus(); } else if (!e.shiftKey && document.activeElement === f[f.length - 1]) { e.preventDefault(); f[0].focus(); }
      });
      this.appendChild(wrap);
      this.dlg = wrap;
      this.onDocKey = (e) => { if (e.key === 'Escape' && this.dlg && !this.dlg.contains(e.target)) { e.preventDefault(); this.closeDialog(); } };
      document.addEventListener('keydown', this.onDocKey);
      document.documentElement.classList.add('cdt-lock');
      requestAnimationFrame(() => (wrap.querySelector('.cdt-dialog__actions .cdt-btn--primary') || wrap.querySelector('[data-autofocus]') || wrap.querySelector('.cdt-dialog__x')).focus());
    }

    closeDialog(refocus = true) {
      if (!this.dlg) return;
      this.dlg.remove();
      this.dlg = null;
      document.removeEventListener('keydown', this.onDocKey);
      document.documentElement.classList.remove('cdt-lock');
      if (refocus) {
        const t = this.lastFocus && document.contains(this.lastFocus) ? this.lastFocus : this.root.querySelector('.cdt-stage');
        t?.focus({ preventScroll: true });
      }
    }

    depthDialog() {
      let chosen = this.design.prefs.depth;
      const html = `<p class="cdt-muted">Choose the depth for your closet organizer modules.</p><div class="cdt-depths" role="radiogroup" aria-label="Module depth">${this.cfg.depths.map((o, i) => `<label class="cdt-depth${o.value === chosen ? ' is-on' : ''}"><input type="radio" name="cdt-depth" value="${o.value}" ${o.value === chosen ? 'checked' : ''} ${i === 0 ? 'data-autofocus' : ''}><span class="cdt-depth__body"><strong>${this.len(o.value)} Module Depth${o.label ? ` · ${esc(o.label)}` : ''}</strong>${o.desc ? `<span class="cdt-muted">${esc(o.desc)}</span>` : ''}</span>${o.tag ? `<em class="cdt-depth__tag">${esc(o.tag)}</em>` : ''}</label>`).join('')}</div><p class="cdt-muted cdt-small">Sample depths for this demo. You can change the depth later in Preferences.</p>`;
      this.openDialog({
        title: 'Select Preferred Module Depth',
        html,
        actions: [
          { label: 'Cancel' },
          { label: 'Apply & Continue', variant: 'primary', noRefocus: true, onClick: (wrap) => {
            const r = wrap.querySelector('input[name="cdt-depth"]:checked');
            chosen = r ? +r.value : chosen;
            if (chosen !== this.design.prefs.depth) { this.push(); this.design.prefs.depth = chosen; }
            this.closeDialog(false);
            this.goStep(3);
            return true;
          } },
        ],
      });
      this.dlg.addEventListener('change', (e) => {
        if (e.target.name !== 'cdt-depth') return;
        this.dlg.querySelectorAll('.cdt-depth').forEach((l) => l.classList.toggle('is-on', l.contains(e.target)));
      });
    }

    startDialog() {
      this.openDialog({
        title: 'How would you like to start?',
        html: `<p>${esc(this.cfg.text.startQuestion)}</p>`,
        actions: [
          { label: 'Start from scratch', noRefocus: true, onClick: () => { this.ui.tab[5] = 'modules'; this.closeDialog(false); this.goStep(5); return true; } },
          { label: 'Auto populate', variant: 'primary', noRefocus: true, onClick: () => { this.closeDialog(false); this.ui.tab[5] = 'prefs'; this.goStep(5); this.autoPopulate(); return true; } },
        ],
      });
    }

    confirm(title, text, label, fn) {
      this.openDialog({ title, html: `<p class="cdt-alert">${I.warn}<span>${esc(text)}</span></p>`, actions: [{ label: 'Cancel' }, { label, variant: 'primary', onClick: fn }] });
    }

    summaryDialog() {
      const b = this.bom();
      const v = this.validate();
      const d = this.design;
      const rows = b.rows.map((r) => `<tr><th scope="row">${esc(r.name)}${r.required ? ' <span class="cdt-req">Required</span>' : ''}</th><td>${esc(r.detail)}</td><td class="cdt-num">${r.qty}</td><td class="cdt-num">${this.money.format(r.unit / 100)}</td><td class="cdt-num">${this.money.format((r.unit * r.qty) / 100)}</td></tr>`).join('');
      const issues = (arr, cls, title) => (arr.length ? `<div class="cdt-check is-${cls}"><p><strong>${title}</strong></p><ul>${arr.map((x) => `<li>${esc(x)}</li>`).join('')}</ul></div>` : '');
      const html = `<p class="cdt-muted">${esc(this.line().name)} · ${esc(SHAPES.find((s) => s.id === d.shape)?.label || '')} · Wall B ${this.len(d.room.width)} · Depth ${this.len(d.room.depth)} · Height ${this.len(d.room.height)} · Door ${esc(DOORS.find((x) => x.id === d.door.type)?.label || '')} ${this.len(d.door.width)}</p>${b.rows.length ? `<div class="cdt-table-wrap"><table class="cdt-table"><thead><tr><th scope="col">Item</th><th scope="col">Details</th><th scope="col" class="cdt-num">Qty</th><th scope="col" class="cdt-num">Unit</th><th scope="col" class="cdt-num">Total</th></tr></thead><tbody>${rows}</tbody><tfoot><tr><th scope="row" colspan="4">Sample subtotal</th><td class="cdt-num"><strong>${this.money.format(b.total / 100)}</strong></td></tr></tfoot></table></div>` : '<p>Your design has no modules yet.</p>'}${issues(v.errors, 'error', 'Needs fixing before checkout')}${issues(v.warnings, 'warn', 'Please check')}<div class="cdt-check is-info"><p><strong>Not checked by this tool</strong></p><ul><li>Wall structure, anchoring and on-site installation conditions.</li></ul></div><p class="cdt-muted cdt-small">${esc(this.cfg.text.demoNote)}</p>`;
      this.openDialog({ title: 'Your design summary', html, wide: true, cls: 'cdt-modal--summary', actions: [{ label: 'Print summary', onClick: () => { this.print(); return true; } }, { label: 'Close', variant: 'primary' }] });
    }

    print() {
      const html = document.documentElement;
      html.classList.add('cdt-printing');
      const done = () => { html.classList.remove('cdt-printing'); window.removeEventListener('afterprint', done); };
      window.addEventListener('afterprint', done);
      window.print();
      setTimeout(done, 1500);
    }

    cartDialog() {
      const t = this.cfg.text;
      const acts = [{ label: 'Save design', onClick: () => { this.saveNow(); } }];
      if (t.contactLink) acts.push({ label: t.contactLabel || 'Contact us', href: t.contactLink });
      acts.push({ label: 'View summary', variant: 'primary', noRefocus: true, onClick: () => { this.closeDialog(false); this.summaryDialog(); return true; } });
      this.openDialog({ title: t.cartTitle, html: `<p>${esc(t.cartText)}</p>`, actions: acts });
    }

    saveNow() {
      if (this.editor) { this.toast('Saving is turned off in the theme editor.'); return; }
      if (this.persist()) this.toast('Design saved on this device.');
      else this.toast('Could not save on this device. Check that your browser allows site storage.', 'error');
    }

    /* ---------- events ---------- */
    bind() {
      this.root.addEventListener('click', (e) => {
        const el = e.target.closest('[data-action]');
        if (!el || !this.root.contains(el) || el.disabled) return;
        if (el.getAttribute('aria-disabled') === 'true' && el.dataset.action !== 'cart') return;
        this.act(el.dataset.action, el);
      });
      this.root.addEventListener('change', (e) => {
        const el = e.target;
        if (el.dataset.field) this.onField(el);
        else if (el.dataset.select) this.onSelect(el);
        else if (el.dataset.color) this.change((s) => { s.room.color = el.value; }, '', true);
      });
      this.root.addEventListener('keydown', (e) => this.onKey(e));
      this.root.addEventListener('pointerdown', (e) => this.onDown(e));
      this.root.addEventListener('pointermove', (e) => this.onMove(e));
      this.root.addEventListener('pointerup', (e) => this.onUp(e));
      this.root.addEventListener('pointercancel', (e) => this.onUp(e, true));
      this.onDocDown = () => { this.ptrDown = true; };
      this.onDocUp = () => {
        setTimeout(() => {
          this.ptrDown = false;
          if (this.pending) this.render();
        }, 0);
      };
      document.addEventListener('pointerdown', this.onDocDown, true);
      document.addEventListener('pointerup', this.onDocUp, true);
      document.addEventListener('pointercancel', this.onDocUp, true);
    }

    act(a, el) {
      const d = this.design;
      const ds = el.dataset;
      switch (a) {
        case 'goto': this.goStep(+ds.step); break;
        case 'next': this.next(); break;
        case 'prev': this.prev(); break;
        case 'line': {
          if (ds.id === d.line) break;
          const target = this.cfg.lines.find((l) => l.id === ds.id);
          const name = String(target?.name || '').toLowerCase();
          const lost = d.modules.filter((m) => { const def = this.def(m.type); return def && def.lines.length && !def.lines.includes(name); });
          if (lost.length) {
            this.confirm('Change product line?', `${lost.length} module(s) in your design are not part of ${target.name} and will be removed. You can undo this.`, 'Change product line', () => this.change((s) => { s.line = ds.id; s.modules = s.modules.filter((m) => !lost.some((x) => x.id === m.id)); }));
          } else this.change((s) => { s.line = ds.id; });
          break;
        }
        case 'shape': this.change((s) => { s.shape = ds.id; }); break;
        case 'door-type': this.change((s) => { s.door.type = ds.id; }); break;
        case 'tab': this.ui.tab[this.ui.step] = ds.tab; this.render(); break;
        case 'view': this.ui.view[this.ui.step] = ds.view; this.render(); break;
        case 'unit': this.ui.unit = ds.unit; this.persist(); this.render(); break;
        case 'undo': this.undo(); break;
        case 'redo': this.redo(); break;
        case 'save': this.saveNow(); break;
        case 'restart':
          this.confirm('Start over?', 'This clears your current design and the copy saved on this device.', 'Start over', () => {
            try { localStorage.removeItem(this.key); } catch (e) { /* storage unavailable */ }
            this.design = this.blank();
            this.past = [];
            this.future = [];
            this.ui.maxStep = 1;
            this.goStep(1);
            this.toast('Started a new design.');
          });
          break;
        case 'collapse': this.ui.collapsed = true; this.render(); this.root.querySelector('[data-action="expand"]')?.focus(); break;
        case 'expand': this.ui.collapsed = false; this.render(); this.root.querySelector('[data-action="collapse"]')?.focus(); break;
        case 'door-center': this.change((s) => { s.door.offset = null; }); break;
        case 'wall-reset': this.change((s) => { s.room.color = this.cfg.room.color; }); break;
        case 'add-feat': {
          const F = FEATURES[ds.type];
          const id = uid();
          const x = Math.max(0, Math.round((d.room.width - toU(F.w)) / 2) + toU((d.features.length % 4) * 6));
          const y = Math.min(toU(F.y), Math.max(0, d.room.height - toU(F.h)));
          this.ui.selFeat = id;
          if (this.ui.view[4] === 'plan') this.ui.view[4] = 'wall';
          this.change((s) => { s.features.push({ id, type: ds.type, x: Math.min(x, s.room.width - toU(F.w)), y }); }, `${F.label} added to Wall B. Drag it in the wall view or type its position.`);
          break;
        }
        case 'select-feat': this.ui.selFeat = ds.id; if (this.ui.view[4] === 'plan') this.ui.view[4] = 'wall'; this.render(); break;
        case 'remove-feat': this.change((s) => { s.features = s.features.filter((f) => f.id !== ds.id); }, 'Removed.'); break;
        case 'add-module': this.addModule(ds.id); break;
        case 'open-modules': this.ui.tab[5] = 'modules'; this.ui.collapsed = false; this.render(); this.root.querySelector('.cdt-mcard')?.focus(); break;
        case 'select': this.ui.sel = ds.inst; this.ui.tab[5] = 'modules'; this.render(); break;
        case 'deselect': this.ui.sel = null; this.render(); break;
        case 'move': if (this.ui.sel) this.moveBy(this.ui.sel, +ds.dir * U); break;
        case 'dup': {
          const m = d.modules.find((q) => q.id === this.ui.sel);
          if (!m) break;
          const x = this.findGap(m.width);
          if (x == null) { this.toast('There is no free space on the wall for a copy.', 'error'); break; }
          const id = uid();
          this.change((s) => { s.modules.push({ ...m, id, x }); }, 'Module duplicated.');
          this.ui.sel = id;
          this.render();
          break;
        }
        case 'remove': {
          const id = this.ui.sel;
          this.ui.sel = null;
          this.change((s) => { s.modules = s.modules.filter((q) => q.id !== id); }, 'Module removed. Use Undo to restore it.');
          break;
        }
        case 'pref': this.change((s) => { s.prefs[ds.pref] = ds.value; }); break;
        case 'acc': {
          const acc = this.cfg.accessories.find((x) => x.id === ds.id);
          this.change((s) => { s.acc[ds.id] = clamp((s.acc[ds.id] || 0) + +ds.delta, 0, acc ? acc.max : 10); });
          break;
        }
        case 'clear':
          this.confirm('Clear Modules', 'This action will remove all modules and accessories from the current layout. Your room, door and wall settings stay the same. Do you want to continue?', 'Confirm', () => {
            this.ui.sel = null;
            this.change((s) => { s.modules = []; s.acc = {}; }, 'All modules cleared. Use Undo to bring them back.');
          });
          break;
        case 'auto':
          if (d.modules.length) this.confirm('Replace your modules?', 'Auto populate replaces the modules in your design with a suggested layout. You can undo this.', 'Auto populate', () => { this.autoPopulate(); });
          else this.autoPopulate();
          break;
        case 'summary': this.summaryDialog(); break;
        case 'cart': if (d.modules.length) this.cartDialog(); else this.toast('Add at least one module first.'); break;
        case 'focus-field': {
          this.ui.tab[4] = 'measure';
          if (this.ui.step !== 4) break;
          this.render();
          const inp = this.root.querySelector(`[data-field="${CSS.escape(ds.target)}"]`);
          inp?.focus();
          inp?.select();
          break;
        }
        default:
      }
    }

    onField(el) {
      const name = el.dataset.field;
      const err = this.root.querySelector(`#${CSS.escape(el.id)}-err`);
      const showErr = (msg) => { el.setAttribute('aria-invalid', 'true'); if (err) err.textContent = msg; };
      const r = parseLength(el.value, this.ui.unit);
      if (r.error) return showErr(r.error);
      const v = r.value;
      const d = this.design;
      const [key, id] = name.split(':');
      const F = id ? FEATURES[d.features.find((f) => f.id === id)?.type] : null;
      const sel = d.modules.find((m) => m.id === this.ui.sel);
      const spec = {
        'room.width': [24, 360], 'room.depth': [12, 120], 'room.height': [60, 144],
        'door.width': [18, inch(d.room.width)], 'door.offset': [0, inch(d.room.width - d.door.width)],
        'feat.x': [0, F ? inch(d.room.width) - F.w : 0], 'feat.y': [0, F ? inch(d.room.height) - F.h : 0],
        'mod.x': [0, sel ? inch(d.room.width - sel.width) : 0],
      }[key];
      if (!spec) return undefined;
      const [lo, hi] = spec.map(toU);
      if (v < lo) return showErr(`Must be at least ${this.len(lo)}.`);
      if (v > hi) return showErr(`Must be ${this.len(Math.max(hi, 0))} or less.`);
      if (key === 'mod.x' && sel) {
        const clash = d.modules.some((q) => q.id !== sel.id && v < q.x + q.width && v + sel.width > q.x);
        if (clash) return showErr('That position overlaps another module.');
      }
      this.change((s) => {
        if (key === 'room.width') { s.room.width = v; s.door.width = Math.min(s.door.width, v); if (s.door.offset != null) s.door.offset = clamp(s.door.offset, 0, v - s.door.width); }
        if (key === 'room.depth') s.room.depth = v;
        if (key === 'room.height') s.room.height = v;
        if (key === 'door.width') { s.door.width = v; if (s.door.offset != null) s.door.offset = clamp(s.door.offset, 0, s.room.width - v); }
        if (key === 'door.offset') s.door.offset = v;
        if (key === 'feat.x' || key === 'feat.y') { const f = s.features.find((q) => q.id === id); if (f) f[key === 'feat.x' ? 'x' : 'y'] = v; }
        if (key === 'mod.x' && sel) s.modules.find((q) => q.id === sel.id).x = v;
      }, '', true);
      if (key === 'room.width' || key === 'room.height' || key === 'room.depth') {
        const bad = this.validate().errors.length;
        if (bad && this.design.modules.length) this.toast(`Your room changed. ${bad} module issue(s) need attention in the design step.`, 'error');
      }
      return undefined;
    }

    onSelect(el) {
      const k = el.dataset.select;
      if (k === 'cam') {
        this.ui.cam = el.value;
        [this.ui.yaw, this.ui.pitch] = CAMS[el.value] || [0, 0];
        this.applyCam();
        return;
      }
      if (k === 'pref.depth' || k === 'pref.height') { this.change((s) => { s.prefs[k.split('.')[1]] = +el.value; }); return; }
      if (k === 'mod.width') {
        const m = this.design.modules.find((q) => q.id === this.ui.sel);
        if (m) this.setWidth(m, +el.value);
      }
    }

    onKey(e) {
      const radio = e.target.closest('[role="radio"]');
      if (radio && ['ArrowLeft', 'ArrowRight', 'ArrowUp', 'ArrowDown'].includes(e.key)) {
        const group = [...radio.closest('[role="radiogroup"]').querySelectorAll('[role="radio"]:not([aria-disabled="true"])')];
        const i = group.indexOf(radio);
        const nx = group[(i + (e.key === 'ArrowRight' || e.key === 'ArrowDown' ? 1 : -1) + group.length) % group.length];
        e.preventDefault();
        nx.click();
        this.root.querySelector(`[data-key="${CSS.escape(nx.dataset.key)}"]`)?.focus();
        return;
      }
      if (e.target.matches('[data-field]') && e.key === 'Enter') {
        e.preventDefault();
        const k = e.target.dataset.key;
        e.target.blur();
        this.root.querySelector(`[data-key="${CSS.escape(k)}"]`)?.focus();
        return;
      }
      if (!e.target.matches('[data-canvas]')) return;
      const step = e.shiftKey ? 6 * U : U;
      if (this.ui.sel && (e.key === 'ArrowLeft' || e.key === 'ArrowRight')) { e.preventDefault(); this.moveBy(this.ui.sel, e.key === 'ArrowLeft' ? -step : step); }
      else if (this.ui.selFeat && ['ArrowLeft', 'ArrowRight', 'ArrowUp', 'ArrowDown'].includes(e.key)) {
        e.preventDefault();
        const f = this.design.features.find((q) => q.id === this.ui.selFeat);
        const F = FEATURES[f.type];
        const dx = e.key === 'ArrowLeft' ? -step : e.key === 'ArrowRight' ? step : 0;
        const dy = e.key === 'ArrowUp' ? step : e.key === 'ArrowDown' ? -step : 0;
        this.change((s) => { const t = s.features.find((q) => q.id === f.id); t.x = clamp(t.x + dx, 0, s.room.width - toU(F.w)); t.y = clamp(t.y + dy, 0, s.room.height - toU(F.h)); });
      } else if ((e.key === 'Delete' || e.key === 'Backspace') && this.ui.sel) { e.preventDefault(); this.act('remove', { dataset: {} }); }
      else if (e.key === 'Escape' && (this.ui.sel || this.ui.selFeat)) { this.ui.sel = null; this.ui.selFeat = null; this.render(); }
    }

    svgPt(e) {
      const svgEl = this.root.querySelector('[data-svg]');
      if (!svgEl) return null;
      const m = svgEl.getScreenCTM();
      if (!m) return null;
      const p = new DOMPoint(e.clientX, e.clientY).matrixTransform(m.inverse());
      return { x: p.x, y: p.y };
    }

    onDown(e) {
      const host = e.target.closest('[data-canvas]');
      if (!host || e.button > 0 || e.target.closest('[data-action]')) return;
      const v = this.view();
      if (v === '3d') {
        this.drag = { kind: 'orbit', sx: e.clientX, sy: e.clientY, yaw: this.ui.yaw, pitch: this.ui.pitch, moved: false, target: e.target, host };
        host.setPointerCapture(e.pointerId);
        return;
      }
      const pt = this.svgPt(e);
      if (!pt) return;
      const g = e.target.closest('[data-inst]');
      const f = e.target.closest('[data-feat]');
      if (g) {
        const m = this.design.modules.find((q) => q.id === g.dataset.inst);
        if (!m) return;
        const [lo, hi] = this.bounds(m);
        this.drag = { kind: 'module', id: m.id, pt, x0: m.x, lo, hi, moved: false, snap: this.snap(), host };
        this.ui.sel = m.id;
        this.ui.selFeat = null;
      } else if (f && v === 'wall') {
        const ft = this.design.features.find((q) => q.id === f.dataset.feat);
        this.drag = { kind: 'feat', id: ft.id, pt, x0: ft.x, y0: ft.y, moved: false, snap: this.snap(), host };
        this.ui.selFeat = ft.id;
        this.ui.sel = null;
      } else {
        this.drag = { kind: 'none', host };
      }
      host.setPointerCapture(e.pointerId);
      e.preventDefault();
    }

    onMove(e) {
      const dr = this.drag;
      if (!dr || dr.kind === 'none') return;
      if (dr.kind === 'orbit') {
        const dx = e.clientX - dr.sx;
        const dy = e.clientY - dr.sy;
        if (!dr.moved && Math.hypot(dx, dy) < 4) return;
        dr.moved = true;
        this.ui.yaw = clamp(dr.yaw + dx * 0.3, -50, 50);
        this.ui.pitch = clamp(dr.pitch - dy * 0.2, -18, 14);
        this.ui.cam = 'custom';
        this.applyCam();
        return;
      }
      const pt = this.svgPt(e);
      if (!pt) return;
      const dx = toU(pt.x - dr.pt.x);
      if (dr.kind === 'module') {
        const m = this.design.modules.find((q) => q.id === dr.id);
        if (dr.hi < dr.lo) return;
        const x = clamp(Math.round((dr.x0 + dx) / SNAP) * SNAP, dr.lo, dr.hi);
        if (x !== m.x) { m.x = x; dr.moved = true; this.redrawCanvas(); }
      } else if (dr.kind === 'feat') {
        const f = this.design.features.find((q) => q.id === dr.id);
        const F = FEATURES[f.type];
        const dy = toU(dr.pt.y - pt.y);
        const x = clamp(Math.round((dr.x0 + dx) / SNAP) * SNAP, 0, this.design.room.width - toU(F.w));
        const y = clamp(Math.round((dr.y0 + dy) / SNAP) * SNAP, 0, this.design.room.height - toU(F.h));
        if (x !== f.x || y !== f.y) { f.x = x; f.y = y; dr.moved = true; this.redrawCanvas(); }
      }
    }

    onUp(e, cancel = false) {
      const dr = this.drag;
      if (!dr) return;
      this.drag = null;
      try { dr.host.releasePointerCapture(e.pointerId); } catch (err) { /* already released */ }
      if (dr.kind === 'orbit') {
        if (!dr.moved && !cancel) {
          const hit = document.elementFromPoint(e.clientX, e.clientY)?.closest('[data-inst]');
          this.ui.sel = hit ? hit.dataset.inst : null;
          if (hit && this.ui.step === 5) this.ui.tab[5] = 'modules';
          this.render();
        } else this.render();
        return;
      }
      if (dr.kind === 'none') {
        if (this.ui.sel || this.ui.selFeat) { this.ui.sel = null; this.ui.selFeat = null; this.render(); }
        return;
      }
      if (dr.moved) {
        this.push(dr.snap);
        this.scheduleSave();
      }
      if (dr.kind === 'module' && this.ui.step === 5) this.ui.tab[5] = 'modules';
      this.render();
      this.root.querySelector('[data-canvas]')?.focus({ preventScroll: true });
    }

    redrawCanvas() {
      const host = this.root.querySelector('[data-canvas]');
      if (host) host.innerHTML = this.tCanvas();
    }

    /* ---------- page extras ---------- */
    setupMobileNote() {
      const note = this.querySelector('[data-cdt-mnote]');
      if (!note) return;
      let dismissed = false;
      try { dismissed = sessionStorage.getItem('cdt-mnote') === '1'; } catch (e) { /* ignore */ }
      if (dismissed && note.dataset.mode === 'notice') note.hidden = true;
      note.querySelector('[data-cdt-mnote-close]')?.addEventListener('click', () => {
        note.hidden = true;
        try { sessionStorage.setItem('cdt-mnote', '1'); } catch (e) { /* ignore */ }
      });
      const copy = note.querySelector('[data-cdt-copy]');
      copy?.addEventListener('click', async () => {
        const url = copy.dataset.url || location.href;
        try { await navigator.clipboard.writeText(url); copy.textContent = 'Copied!'; } catch (e) { copy.textContent = 'Copy failed'; }
        setTimeout(() => { copy.textContent = 'Copy'; }, 2000);
      });
    }

    setupEditor() {
      if (!this.editor) return;
      this.onEditor = (e) => {
        const id = e.detail && e.detail.blockId;
        if (!id) return;
        if (this.cfg.lines.some((l) => l.id === id)) { this.design.line = id; this.goStep(1); }
        else if (this.cfg.modules.some((m) => m.id === id)) { this.ui.maxStep = 5; this.ui.tab[5] = 'modules'; this.goStep(5); }
        else if (this.cfg.accessories.some((a) => a.id === id)) { this.ui.maxStep = 5; this.ui.tab[5] = 'acc'; this.goStep(5); }
      };
      document.addEventListener('shopify:block:select', this.onEditor);
    }
  }

  customElements.define('ceme-design-tool', CemeDesignTool);
})();
